// frontend/src/components/admin/ScreensList.jsx
import React, { useEffect, useState } from "react";
import {
  Card,
  Button,
  Table,
  Badge,
  Modal,
  Form,
  Spinner,
  Alert,
} from "react-bootstrap";
import { API_URL } from "../../apiConfig";
import { clearSession } from "../../auth";
import ToastNotification from "../ToastNotification";
import {
  ArrowPathIcon,
  LinkIcon,
  ExclamationTriangleIcon,
  GlobeAltIcon,
  ComputerDesktopIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  BoltIcon,
  ArrowsUpDownIcon,
  PuzzlePieceIcon,
  WrenchScrewdriverIcon,
  ClockIcon,
  CheckIcon,
  ArrowDownCircleIcon,
  MapPinIcon,
  SwatchIcon,
  MegaphoneIcon,
  ChartBarIcon,
  QueueListIcon,
  CheckCircleIcon,
  MinusCircleIcon,
  FunnelIcon,
} from "@heroicons/react/24/outline";
import TvScreenPreview from "../shared/TvScreenPreview";

const CARD_RADIUS = 20;

const LATEST_PLAYER_VERSION = "1.0.38";

export default function ScreensList() {
  const [screens, setScreens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState(null); // "online" ou "offline"
  const [sortColumn, setSortColumn] = useState("condo");
  const [sortDirection, setSortDirection] = useState("asc"); // "asc" ou "desc"
  const [searchTerm, setSearchTerm] = useState(""); // Filtro de pesquisa multi-uso
  const [alertFilter, setAlertFilter] = useState(null); // Filtro por tipo de alerta: "connection", "sync", "hardware", "browser"
  const [hwFilter, setHwFilter] = useState(""); // Filtro por tipo de hardware: "" = todos, "android", "rpi"
  const [view, setView] = useState("list");

  // Modal de associação
  const [showModal, setShowModal] = useState(false);
  const [bindReturnScreen, setBindReturnScreen] = useState(null);
  const [selectedScreen, setSelectedScreen] = useState(null);
  const [condoSearchTerm, setCondoSearchTerm] = useState("");
  const [condos, setCondos] = useState([]);
  const [searchingCondos, setSearchingCondos] = useState(false);
  const [selectedCondo, setSelectedCondo] = useState(null);
  const [saving, setSaving] = useState(false);

  // Modal de barra inferior (footer)
  const [showFooterModal, setShowFooterModal] = useState(false);
  const [footerScreen, setFooterScreen] = useState(null);
  const [footerMediaAssets, setFooterMediaAssets] = useState([]);
  const [footerMediaAssetId, setFooterMediaAssetId] = useState("");
  const [footerMediaSearch, setFooterMediaSearch] = useState("");
  const [footerDropdownOpen, setFooterDropdownOpen] = useState(false);
  const [footerScope, setFooterScope] = useState("single"); // "single", "condo", "city", "all-online", "all"
  const [affectedScreens, setAffectedScreens] = useState([]);
  const [layoutType, setLayoutType] = useState("layout2"); // "layout2" (Retrato Padrão), "layout3" (Layout Assembleia) ou "layout4" (Retrato Comunicados)
  
  const [savingFooter, setSavingFooter] = useState(false);

  // Modal de criar nova tela
  const [showNewScreenModal, setShowNewScreenModal] = useState(false);
  const [newScreenName, setNewScreenName] = useState("");
  const [newScreenLocationType, setNewScreenLocationType] = useState("AVULSO");
  const [savingNewScreen, setSavingNewScreen] = useState(false);

  // Modal de pareamento por código
  const [showPairModal, setShowPairModal] = useState(false);
  const [pairCode, setPairCode] = useState("");
  const [pairName, setPairName] = useState("");
  const [pairLocationType, setPairLocationType] = useState("AVULSO");
  const [clainingPair, setClainingPair] = useState(false);
  const [pairCondoSearchTerm, setPairCondoSearchTerm] = useState("");
  const [pairCondos, setPairCondos] = useState([]);
  const [pairSelectedCondo, setPairSelectedCondo] = useState(null);
  const [pairSearchingCondos, setPairSearchingCondos] = useState(false);

  function autoFormatPairCode(raw) {
    return raw.replace(/\D/g, '').slice(0, 6);
  }

  async function searchPairCondos(term) {
    if (!term || term.length < 2) { setPairCondos([]); return; }
    setPairSearchingCondos(true);
    try {
      const url = `${API_URL}/admin/superlogica/condos?search=${encodeURIComponent(term)}`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const data = await r.json();
      setPairCondos(data.condos || []);
    } catch { setPairCondos([]); } finally { setPairSearchingCondos(false); }
  }
  function handlePairCondoSearchChange(value) {
    setPairCondoSearchTerm(value);
    searchPairCondos(value);
  }
  function handleSelectPairCondo(condo) {
    setPairSelectedCondo(condo);
    setPairCondoSearchTerm("");
    setPairCondos([]);
  }
  function resetPairModal() {
    setPairCode("");
    setPairName("");
    setPairLocationType("AVULSO");
    setPairSelectedCondo(null);
    setPairCondoSearchTerm("");
    setPairCondos([]);
  }

  // Modal de editar nome
  const [showEditNameModal, setShowEditNameModal] = useState(false);
  const [editingScreen, setEditingScreen] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [savingEditName, setSavingEditName] = useState(false);

  // Modal de saúde do sistema
  const [showHealthModal, setShowHealthModal] = useState(false);
  const [healthScreen, setHealthScreen] = useState(null);
  const [healthHistory, setHealthHistory] = useState([]);
  const [healthPeaks, setHealthPeaks] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [uptimeStats, setUptimeStats] = useState(null);
  const [healthPreviewFallback, setHealthPreviewFallback] = useState(null);

  // Modal de saúde do browser
  const [showBrowserHealthModal, setShowBrowserHealthModal] = useState(false);
  const [browserHealthScreen, setBrowserHealthScreen] = useState(null);
  const [browserHealthStatus, setBrowserHealthStatus] = useState(null);
  const [browserHealthLoading, setBrowserHealthLoading] = useState(false);
  const [browserHealthActionLogs, setBrowserHealthActionLogs] = useState([]);
  const [browserHealthCheckingAction, setBrowserHealthCheckingAction] = useState(null);

  // Estado do Toast
  const [toast, setToast] = useState({ show: false, title: "", message: "", variant: "success" });

  // Estado do Modal de Confirmação
  const [confirmModal, setConfirmModal] = useState({ show: false, title: "", message: "", onConfirm: null });
  const [layoutNoticeWarning, setLayoutNoticeWarning] = useState({ show: false, notices: [], newLayout: "", onConfirm: null });

  const token = localStorage.getItem("token");

  // Função para exibir toast
  function showToast(message, variant = "success") {
    const titles = {
      success: "✓ Sucesso",
      error: "✕ Erro",
      warning: "⚠ Atenção",
      info: "ℹ Informação"
    };
    setToast({
      show: true,
      title: titles[variant] || titles.success,
      message,
      variant: variant === "error" ? "danger" : variant
    });
  }

  // Função para exibir modal de confirmação
  function layoutTypeLabel(lt) {
    switch (lt) {
      case "layout2": return "Retrato Padrão";
      case "layout3": return "Layout Assembleia";
      case "layout4": return "Retrato Comunicados";
      default: return lt || "—";
    }
  }

  function showLayoutNoticeWarning(notices, newLayout) {
    return new Promise((resolve) => {
      setLayoutNoticeWarning({
        show: true,
        notices,
        newLayout,
        onConfirm: (confirmed) => {
          setLayoutNoticeWarning({ show: false, notices: [], newLayout: "", onConfirm: null });
          resolve(confirmed);
        },
      });
    });
  }

  function showConfirm(title, message) {
    return new Promise((resolve) => {
      setConfirmModal({
        show: true,
        title,
        message,
        onConfirm: (confirmed) => {
          setConfirmModal({ show: false, title: "", message: "", onConfirm: null });
          resolve(confirmed);
        }
      });
    });
  }

  // Helper para detectar e tratar erros de autenticação
  function handleAuthError(errorMessage) {
    if (errorMessage?.includes("Token") || errorMessage?.includes("401")) {
      clearSession();
      showToast("Sua sessão expirou. Faça login novamente.", "error");
      window.location.href = "/";
      return true;
    }
    return false;
  }

  useEffect(() => {
    loadScreens();
    const interval = setInterval(() => loadScreens(true), 30000);
    return () => clearInterval(interval);
  }, []);

  // Auto-refresh quando modal de saúde está aberto
  useEffect(() => {
    if (showHealthModal && healthScreen) {
      const screenId = healthScreen._id;
      const previewInterval = setInterval(() => {
        loadEstimatedContentPreview(screenId);
      }, 10000);

      const interval = setInterval(async () => {
        // Atualiza tabela + picos
        loadHealthHistory(screenId);
        // Atualiza disponibilidade
        loadUptimeStats(screenId);
        // Atualiza KPIs (disco, temp, RAM) — busca dados frescos da tela
        const list = await loadScreens();
        if (list) {
          const updated = list.find((s) => s._id === screenId);
          if (updated) setHealthScreen(updated);
        }
      }, 30000); // Atualiza a cada 30 segundos

      return () => {
        clearInterval(interval);
        clearInterval(previewInterval);
      };
    }
  }, [showHealthModal, healthScreen?._id]);

  async function loadScreens(silent = false) {
    if (!silent) setLoading(true);
    setError("");
    try {
      const r = await fetch(`${API_URL}/admin/screens`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!r.ok) {
        const data = await r.json();
        throw new Error(data.message || "Erro ao carregar telas");
      }

      const data = await r.json();
      const list = data.screens || [];
      setScreens(list);
      return list;
    } catch (e) {
      console.error(e);
      setError(e.message || "Erro ao carregar telas");
      return null;
    } finally {
      if (!silent) setLoading(false);
    }
  }

  function openBindModal(screen) {
    setSelectedScreen(screen);
    setCondoSearchTerm("");
    setCondos([]);
    setSelectedCondo(null);
    setShowModal(true);
  }

  function closeModal() {
    setShowModal(false);
    setSelectedScreen(null);
    setCondoSearchTerm("");
    setCondos([]);
    setSelectedCondo(null);
    if (bindReturnScreen) {
      const s = bindReturnScreen;
      setBindReturnScreen(null);
      openHealthModal(s);
    }
  }

  async function loadFooterMediaAssets() {
    try {
      const r = await fetch(`${API_URL}/admin/media-assets?active=true&type=IMAGE`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) return;
      const data = await r.json();
      const footerBars = (data.mediaAssets || [])
        .filter(m => m.width && m.height && m.height > 0 && m.width / m.height >= 9)
        .sort((a, b) => (a.name || "").localeCompare(b.name || "", "pt-BR"));
      setFooterMediaAssets(footerBars);
    } catch (e) {
      console.error("Erro ao carregar mídias para barra:", e);
    }
  }

  function openFooterModal(screen) {
    setFooterScreen(screen);
    setFooterMediaAssetId(screen.footerMediaAssetId || "");
    setFooterMediaSearch("");
    setFooterScope("single");
    setLayoutType(screen.layoutType || "layout2");
    setAffectedScreens([screen]);
    setShowFooterModal(true);
    loadFooterMediaAssets();
  }

  // Calcula as telas que serão afetadas baseado no escopo selecionado
  function calculateAffectedScreens(scope, currentScreen) {
    if (!currentScreen) return [];
    
    switch(scope) {
      case "single":
        return [currentScreen];
      
      case "condo":
        if (!currentScreen.superlogicaCondoId) {
          return [currentScreen]; // Se não tem condomínio, só a tela atual
        }
        return screens.filter(s => s.superlogicaCondoId === currentScreen.superlogicaCondoId);
      
      case "city":
        if (!currentScreen.city) {
          return [currentScreen]; // Se não tem cidade, só a tela atual
        }
        return screens.filter(s => s.city === currentScreen.city && s.uf === currentScreen.uf);
      
      case "all-online":
        return screens.filter(s => s.status === "online");
      
      case "all":
        return screens;
      
      default:
        return [currentScreen];
    }
  }

  // Atualiza as telas afetadas quando o escopo muda
  function handleScopeChange(newScope) {
    setFooterScope(newScope);
    const affected = calculateAffectedScreens(newScope, footerScreen);
    setAffectedScreens(affected);
  }

  function closeFooterModal() {
    setShowFooterModal(false);
    setFooterScreen(null);
    setFooterMediaAssetId("");
    setFooterMediaAssets([]);
    setFooterMediaSearch("");
    setFooterDropdownOpen(false);
    setFooterScope("single");
    setLayoutType("layout2");
    setAffectedScreens([]);
  }

  async function handleSaveFooter() {
    if (!footerScreen || affectedScreens.length === 0) return;
    
    const confirmed = affectedScreens.length > 1 
      ? await showConfirm(
          "Aplicar Configuração",
          `Aplicar esta configuração para ${affectedScreens.length} tela(s)?\n\nTelas afetadas:\n${affectedScreens.slice(0, 10).map(s => `• ${s.name || s.token.substring(0, 10)}`).join('\n')}${affectedScreens.length > 10 ? '\n...e mais ' + (affectedScreens.length - 10) + ' tela(s)' : ''}`
        )
      : true;
    
    if (!confirmed) return;

    // Verificar comunicados IMAGE_ONLY que ficam incompatíveis com o novo layout
    if (footerScope === "single" && footerScreen && layoutType !== (footerScreen.layoutType || "layout2")) {
      try {
        const r = await fetch(`${API_URL}/admin/condo-notices`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.ok) {
          const data = await r.json();
          const allNotices = data.notices || [];
          const screen = footerScreen;
          const incompatible = allNotices.filter((notice) => {
            const img = notice.imageAssetId;
            if (!img || typeof img !== "object") return false;
            if (notice.text) return false; // TEXT_IMAGE não é afetado
            const compat = img.compatibleLayouts || [];
            if (compat.length === 0) return false; // não classificada
            if (compat.includes(layoutType)) return false; // compatível com novo layout
            // Verificar se este comunicado se aplica à tela
            const refs = Array.isArray(notice.scopeRef) ? notice.scopeRef : notice.scopeRef ? [notice.scopeRef] : [];
            if (notice.scopeType === "ALL") return true;
            if (notice.scopeType === "CONDO") return refs.includes(String(screen.superlogicaCondoId || ""));
            if (notice.scopeType === "CITY") {
              const city = screen.superlogicaCondoCity || screen.city || "";
              const uf = screen.superlogicaCondoUf || screen.uf || "";
              return refs.includes(`${city}|${uf}`);
            }
            if (notice.scopeType === "SCREEN") return refs.includes(String(screen._id || ""));
            return false;
          });
          if (incompatible.length > 0) {
            const proceed = await showLayoutNoticeWarning(incompatible, layoutType);
            if (!proceed) return;
          }
        }
      } catch (e) {
      }
    }

    setSavingFooter(true);
    try {
      const payload = {
        footerMediaAssetId: footerMediaAssetId || null,
        footerHeightPx: 120, // Altura fixa
        ...(footerScope === "single" ? { layoutType } : {}),
        screenIds: affectedScreens.map(s => s._id),
      };

      const r = await fetch(`${API_URL}/admin/screens/bulk/footer`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!r.ok) {
        const data = await r.json();
        throw new Error(data.message || "Erro ao salvar barra");
      }

      const result = await r.json();
      showToast(`Configuração aplicada com sucesso em ${result.updated || affectedScreens.length} tela(s)!`, "success");
      
      await loadScreens();
      closeFooterModal();
    } catch (e) {
      console.error(e);
      showToast(e.message || "Erro ao salvar barra", "error");
    } finally {
      setSavingFooter(false);
    }
  }

  async function openHealthModal(screen) {
    setHealthScreen(screen);
    setHealthPreviewFallback(null);
    setShowHealthModal(true);
    loadHealthHistory(screen._id);
    loadUptimeStats(screen._id);
    setBrowserHealthScreen(screen);
    setBrowserHealthStatus(null);
    setBrowserHealthActionLogs([]);
    checkBrowserHealth(screen._id, "check");

    const list = await loadScreens();
    if (list) {
      const updated = list.find((s) => s._id === screen._id);
      if (updated) setHealthScreen(updated);
    }

    await loadEstimatedContentPreview(screen._id);
  }

  function closeHealthModal() {
    setShowHealthModal(false);
    setHealthScreen(null);
    setHealthPreviewFallback(null);
    setHealthHistory([]);
    setHealthPeaks(null);
    setUptimeStats(null);
    setBrowserHealthScreen(null);
    setBrowserHealthStatus(null);
    setBrowserHealthActionLogs([]);
  }

  async function loadEstimatedContentPreview(screenId) {
    if (!screenId) return;
    try {
      const r = await fetch(`${API_URL}/admin/screens/${screenId}/content-preview`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) return;
      const data = await r.json();
      if (data?.preview) {
        // Só atualiza se o conteúdo realmente mudou — evita remontar o <video> e "piscar"
        setHealthPreviewFallback((prev) =>
          JSON.stringify(prev) === JSON.stringify(data.preview) ? prev : data.preview
        );
      }
    } catch (e) {
      console.error(e);
    }
  }

  async function loadHealthHistory(screenId) {
    if (!screenId) return;
    setHealthLoading(true);
    try {
      const r = await fetch(
        `${API_URL}/admin/screens/${screenId}/health-history?hours=720&limit=2000`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!r.ok) {
        const data = await r.json();
        throw new Error(data.message || "Erro ao buscar histórico");
      }
      const data = await r.json();
      setHealthHistory(data.samples || []);
      setHealthPeaks(data.peaks || null);
    } catch (e) {
      console.error(e);
      setHealthHistory([]);
      setHealthPeaks(null);
    } finally {
      setHealthLoading(false);
    }
  }

  async function loadUptimeStats(screenId) {
    if (!screenId) return;
    try {
      const r = await fetch(
        `${API_URL}/admin/screens/${screenId}/uptime-stats`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!r.ok) {
        const data = await r.json();
        throw new Error(data.message || "Erro ao buscar estatísticas");
      }
      const data = await r.json();
      setUptimeStats(data.stats || null);
    } catch (e) {
      console.error(e);
      setUptimeStats(null);
    }
  }

  async function openBrowserHealthModal(screen) {
    setBrowserHealthScreen(screen);
    setShowBrowserHealthModal(true);
    setBrowserHealthActionLogs([]);
    setBrowserHealthStatus(null);
    await checkBrowserHealth(screen._id, "check");
  }

  function closeBrowserHealthModal() {
    setShowBrowserHealthModal(false);
    setBrowserHealthScreen(null);
    setBrowserHealthStatus(null);
    setBrowserHealthActionLogs([]);
  }

  async function checkBrowserHealth(screenId, action = "check") {
    if (!screenId) {
      console.error("[checkBrowserHealth] screenId não fornecido!");
      showToast("Erro: Tela não selecionada", "error");
      return;
    }

    // Confirmação para ações críticas
    if (action === "reboot") {
      const screen = screens.find(s => s._id === screenId);
      const screenName = screen?.name || "esta tela";
      const confirmed = await showConfirm(
        "⚠️ Reinicializar Máquina",
        `Confirma REINICIALIZAÇÃO da máquina "${screenName}"?\n\nIsso irá desligar e reiniciar o sistema completamente.\nO processo leva alguns minutos.`
      );
      if (!confirmed) return;
    }

    if (action === "clear-cache") {
      const screen = screens.find(s => s._id === screenId);
      const screenName = screen?.name || "esta tela";
      const confirmed = await showConfirm(
        "Limpar Cache Remoto",
        `Limpar o cache da tela "${screenName}"?\n\nO player apagará o cache local e baixará tudo novamente do servidor.\nA tela ficará no splash por 30–60 segundos.`
      );
      if (!confirmed) return;
    }

    setBrowserHealthCheckingAction(action);
    setBrowserHealthLoading(true);
    
    
    try {
      const url = `${API_URL}/admin/screens/${screenId}/browser-health`;
      
      const r = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ action }),
      });

      
      if (!r.ok) {
        const data = await r.json();
        console.error("[checkBrowserHealth] Erro da API:", data);
        throw new Error(data.message || `Erro ao ${action === 'check' ? 'verificar' : 'executar ação'}`);
      }

      const data = await r.json();
      setBrowserHealthStatus(data);

      // Toast de sucesso
      const actionLabels = {
        check: "verificado",
        restart: "reiniciado",
        "force-open": "forçado",
        reboot: "reiniciando",
        "clear-cache": "cache limpo"
      };
      if (action !== "check") {
        const label = actionLabels[action] || action;
        showToast(`✅ ${action === "clear-cache" ? "Cache limpo — player fará bootstrap em até 15s" : `Browser ${label} com sucesso!`}`, "success");
      }

      // Adiciona ao log de ações
      const newLog = {
        timestamp: new Date().toLocaleTimeString("pt-BR"),
        action,
        result: data.healthStatus || "erro",
        message: data.message,
      };
      setBrowserHealthActionLogs((prev) => [newLog, ...prev]);

      // Se sucesso, recarrega a lista de telas
      if (data.action) {
        await loadScreens();
      }
    } catch (e) {
      console.error("[checkBrowserHealth] Erro capturado:", e);
      console.error("[checkBrowserHealth] Mensagem:", e.message);
      showToast(e.message || "Erro ao executar ação", "error");
      
      const errorLog = {
        timestamp: new Date().toLocaleTimeString("pt-BR"),
        action,
        result: "erro",
        message: e.message,
      };
      setBrowserHealthActionLogs((prev) => [errorLog, ...prev]);
    } finally {
      setBrowserHealthLoading(false);
      setBrowserHealthCheckingAction(null);
    }
  }

  async function handleForceSync(screenId) {
    if (!screenId) return;
    
    const confirmSync = await showConfirm(
      "Forçar Sincronização",
      "Deseja forçar a sincronização de conteúdo desta tela?\n\nIsso fará o player baixar todos os assets novamente."
    );
    
    if (!confirmSync) return;
    
    try {
      const r = await fetch(`${API_URL}/admin/screens/${screenId}/force-sync`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const data = await r.json();
      
      if (!r.ok) {
        throw new Error(data.message || "Erro ao forçar sincronização");
      }

      showToast("✅ Sincronização forçada com sucesso!", "success");
      
      setTimeout(() => {
        loadScreens();
      }, 2000);
      
    } catch (e) {
      console.error(e);
      showToast(e.message || "Erro ao forçar sincronização", "error");
    }
  }

  async function searchCondosAutocomplete(term) {
    if (!term || term.length < 2) {
      setCondos([]);
      return;
    }

    setSearchingCondos(true);
    try {
      const url = `${API_URL}/admin/superlogica/condos?search=${encodeURIComponent(term)}`;

      const r = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!r.ok) {
        console.error("[ScreensList] Erro na resposta:", r.status);
        setCondos([]);
        return;
      }

      const data = await r.json();
      setCondos(data.condos || []);
    } catch (e) {
      console.error("[ScreensList] Exceção:", e);
      setCondos([]);
    } finally {
      setSearchingCondos(false);
    }
  }

  function handleCondoSearchChange(value) {
    setCondoSearchTerm(value);
    searchCondosAutocomplete(value);
  }

  function handleSelectCondo(condo) {
    setSelectedCondo(condo);
    setCondoSearchTerm(""); // Limpa o termo para não mostrar "nenhum encontrado"
    setCondos([]); // Fecha a lista após selecionar
  }

  async function handleBindCondo() {
    if (!selectedScreen || !selectedCondo) return;

    setSaving(true);
    try {
      const payload = {
        superlogicaCondoId: selectedCondo.id,
        superlogicaCondoName: selectedCondo.name || "",
        addressText: selectedCondo.address || "",
        city: selectedCondo.city || "",
        uf: selectedCondo.uf || "",
      };

      const r = await fetch(
        `${API_URL}/admin/screens/${selectedScreen._id}/bind-condo`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      if (!r.ok) {
        const data = await r.json();
        throw new Error(data.message || "Erro ao vincular condomínio");
      }

      // Recarrega lista
      await loadScreens();
      closeModal();
    } catch (e) {
      console.error(e);
      showToast(e.message || "Erro ao vincular condomínio", "error");
    } finally {
      setSaving(false);
    }
  }

  async function handleClaimPairing() {
    const cleanCode = pairCode.trim();
    if (!/^\d{6}$/.test(cleanCode)) {
      showToast("Código inválido. Deve ser 6 dígitos numéricos", "error");
      return;
    }
    if (!pairName.trim()) {
      showToast("Nome da tela é obrigatório", "error");
      return;
    }
    if (!["CONDOMINIO", "AVULSO"].includes(pairLocationType)) {
      showToast("Tipo de localização inválido", "error");
      return;
    }
    if (pairLocationType === "CONDOMINIO" && !pairSelectedCondo) {
      showToast("Selecione um condomínio", "error");
      return;
    }
    setClainingPair(true);
    try {
      const r = await fetch(`${API_URL}/admin/claim-pairing`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          code: cleanCode,
          name: pairName.trim(),
          locationType: pairLocationType,
          ...(pairLocationType === "CONDOMINIO" && pairSelectedCondo ? {
            superlogicaCondoId: pairSelectedCondo.id,
            superlogicaCondoName: pairSelectedCondo.name || "",
            addressText: pairSelectedCondo.address || "",
            city: pairSelectedCondo.city || "",
            uf: pairSelectedCondo.uf || "",
          } : {}),
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.message || "Erro ao parear tela");
      const tokenMsg = data.screen?.token ? ` | Token: ${data.screen.token}` : '';
      showToast(`✅ ${data.message}${tokenMsg}`, "success");
      resetPairModal();
      setShowPairModal(false);
      await loadScreens();
    } catch (e) {
      console.error(e);
      showToast(e.message || "Erro ao parear tela", "error");
    } finally {
      setClainingPair(false);
    }
  }

  async function handleCreateNewScreen() {
    if (!newScreenName.trim()) {
      showToast("Nome da tela é obrigatório", "error");
      return;
    }

    setSavingNewScreen(true);
    try {
      const r = await fetch(`${API_URL}/admin/screens`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: newScreenName.trim(),
          locationType: newScreenLocationType,
        }),
      });

      if (!r.ok) {
        const data = await r.json();
        throw new Error(data.message || "Erro ao criar tela");
      }

      const data = await r.json();
      showToast(`Tela criada com sucesso! Token: ${data.screen.token}`, "success");
      setNewScreenName("");
      setNewScreenLocationType("AVULSO");
      setShowNewScreenModal(false);
      await loadScreens();
    } catch (e) {
      console.error(e);
      showToast(e.message || "Erro ao criar tela", "error");
    } finally {
      setSavingNewScreen(false);
    }
  }

  function openEditNameModal(screen) {
    setEditingScreen(screen);
    setEditingName(screen.name);
    setShowEditNameModal(true);
  }

  async function handleSaveEditName() {
    if (!editingScreen || !editingName.trim()) {
      showToast("Nome é obrigatório", "error");
      return;
    }

    setSavingEditName(true);
    try {
      const r = await fetch(`${API_URL}/admin/screens/${editingScreen._id}/name`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ name: editingName.trim() }),
      });

      if (!r.ok) {
        const data = await r.json();
        throw new Error(data.message || "Erro ao atualizar nome");
      }

      showToast("Nome atualizado com sucesso!", "success");
      setShowEditNameModal(false);
      setEditingScreen(null);
      setEditingName("");
      await loadScreens();
    } catch (e) {
      console.error(e);
      showToast(e.message || "Erro ao atualizar nome", "error");
    } finally {
      setSavingEditName(false);
    }
  }

  async function handleDeleteScreen(screen) {
    const confirmDelete = await showConfirm(
      "Deletar Tela",
      `Tem certeza que deseja deletar a tela "${screen.name}"?\n\nIsso não pode ser desfeito.`
    );

    if (!confirmDelete) return;

    try {
      const r = await fetch(`${API_URL}/admin/screens/${screen._id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!r.ok) {
        const data = await r.json();
        throw new Error(data.message || "Erro ao deletar tela");
      }

      showToast("Tela deletada com sucesso!", "success");
      await loadScreens();
    } catch (e) {
      console.error(e);
      showToast(e.message || "Erro ao deletar tela", "error");
    }
  }
  function formatDate(dateStr) {
    if (!dateStr) return "—";
    try {
      const d = new Date(dateStr);
      const date = d.toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
      const time = d.toLocaleTimeString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit",
      });
      return `${date} ${time}`;
    } catch {
      return "—";
    }
  }

  function formatUptime(seconds) {
    if (!seconds || seconds < 0) return "—";
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  }

  // Data curta "DD/MM HH:MM" para a coluna Conexão · Sinc.
  function formatShortDate(dateStr) {
    if (!dateStr) return "—";
    try {
      const d = new Date(dateStr);
      const date = d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
      const time = d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
      return `${date} ${time}`;
    } catch {
      return "—";
    }
  }

  // Estados de hardware para os medidores inline da tabela (limites iguais aos do mockup)
  function cpuStateOf(v)  { return v >= 85 ? "crit" : v >= 70 ? "warn" : "ok"; }
  function ramStateOf(p)  { return p >= 88 ? "crit" : p >= 75 ? "warn" : "ok"; }
  function diskStateOf(p) { return p >= 92 ? "crit" : p >= 80 ? "warn" : "ok"; }
  function tempStateOf(v) { return v >= 70 ? "crit" : "ok"; }

  function formatUptimeMs(milliseconds) {
    if (!milliseconds || milliseconds < 0) return "0h 0m";
    const totalSeconds = Math.floor(milliseconds / 1000);
    const days = Math.floor(totalSeconds / (24 * 3600));
    const hours = Math.floor((totalSeconds % (24 * 3600)) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    
    if (days > 0) {
      return `${days} ${days === 1 ? 'dia' : 'dias'} ${hours}h`;
    }
    return `${hours}h ${minutes}m`;
  }

  function getEffectiveNetworkStatus(screen) {
    if (!screen) return null;
    if (screen.networkStatus) return screen.networkStatus;
    if (screen.status === "online") return "online";
    if (screen.status === "offline") return "offline";
    return null;
  }

  // Calcula alertas de equipamento
  function getAlerts() {
    const now = Date.now();
    const fourHoursMs = 4 * 60 * 60 * 1000;
    const tenMinutesMs = 10 * 60 * 1000;

    // Sem conexão: lastSeenAt (ScreenStatus) atualizado a cada heartbeat
    const connectionAlerts = screens.filter(s => {
      const lastSeenAt = s.lastSeenAt ? new Date(s.lastSeenAt).getTime() : 0;
      return lastSeenAt > 0 && (now - lastSeenAt) > fourHoursMs;
    });

    // Sem sincronizar: lastSyncAt (ScreenStatus) atualizado a cada heartbeat/sync-complete
    // Se lastSyncAt é antigo mas lastSeenAt é recente, pode indicar problema de sync
    const syncAlerts = screens.filter(s => {
      const lastSyncAt = s.lastSyncAt ? new Date(s.lastSyncAt).getTime() : 0;
      return lastSyncAt > 0 && (now - lastSyncAt) > fourHoursMs;
    });

    const hardwareAlerts = screens.filter(s => {
      if (!s.status || s.status !== 'online') return false;
      
      const cpuHot = s.cpuTemp && s.cpuTemp >= 70;
      const cpuHigh = s.cpuUsagePct != null && s.cpuUsagePct > 70;
      const ramHigh = s.memoryUsedMb && s.memoryTotalMb && (s.memoryUsedMb / s.memoryTotalMb) > 0.8;
      const diskHigh = s.diskFreeGb && s.diskTotalGb && ((s.diskTotalGb - s.diskFreeGb) / s.diskTotalGb) > 0.8;

      return cpuHot || cpuHigh || ramHigh || diskHigh;
    });

    const browserAlerts = screens.filter(s => {
      if (!s.status || s.status !== 'online') return false;
      
      // Alerta se browserRunning for explicitamente false (dado vindo do heartbeat)
      return s.browserRunning === false;
    });

    return {
      connection: connectionAlerts,
      sync: syncAlerts,
      hardware: hardwareAlerts,
      browser: browserAlerts
    };
  }

  function getFilteredScreens() {
    let filtered = screens;

    // Filtro por status
    if (statusFilter) {
      filtered = filtered.filter(s => s.status === statusFilter);
    }

    // Filtro por alertas
    if (alertFilter) {
      const alerts = getAlerts();
      if (alertFilter === "any-alert") {
        const anyIds = new Set([...alerts.connection, ...alerts.sync, ...alerts.hardware, ...alerts.browser].map(s => s._id));
        filtered = filtered.filter(s => anyIds.has(s._id));
      } else {
        const alertScreenIds = (alerts[alertFilter] || []).map(s => s._id);
        filtered = filtered.filter(s => alertScreenIds.includes(s._id));
      }
    }

    // Filtro por tipo de hardware
    if (hwFilter && hwFilter.trim()) {
      const hw = hwFilter.toLowerCase().trim();
      filtered = filtered.filter(s => (s.playerType || "").toLowerCase().includes(hw));
    }

    // Filtro de pesquisa multi-uso
    if (searchTerm && searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(s => {
        return (
          (s.name || "").toLowerCase().includes(term) ||
          (s.token || "").toLowerCase().includes(term) ||
          (s.playerIp || "").toLowerCase().includes(term) ||
          (s.city || "").toLowerCase().includes(term) ||
          (s.uf || "").toLowerCase().includes(term) ||
          (s.superlogicaCondoId || "").toString().toLowerCase().includes(term) ||
          (s.superlogicaCondoName || "").toLowerCase().includes(term) ||
          (s.playerHostname || "").toLowerCase().includes(term)
        );
      });
    }
    
    return filtered.sort((a, b) => {
      let aVal = "";
      let bVal = "";
      
      switch(sortColumn) {
        case "status":
          aVal = a.status || "";
          bVal = b.status || "";
          break;
        case "name":
          aVal = (a.name || "").toLowerCase();
          bVal = (b.name || "").toLowerCase();
          break;
        case "condo":
          aVal = (a.superlogicaCondoName ? String(a.superlogicaCondoName) : "").toLowerCase();
          bVal = (b.superlogicaCondoName ? String(b.superlogicaCondoName) : "").toLowerCase();
          break;
        case "city":
          aVal = ((a.city || "") + (a.uf || "")).toLowerCase();
          bVal = ((b.city || "") + (b.uf || "")).toLowerCase();
          break;
        case "lastSeen":
          aVal = new Date(a.lastSeenAt || 0).getTime();
          bVal = new Date(b.lastSeenAt || 0).getTime();
          break;
        case "lastSync":
          aVal = new Date(a.lastSyncAt || 0).getTime();
          bVal = new Date(b.lastSyncAt || 0).getTime();
          break;
        case "uptime":
          aVal = a.uptimeSec || 0;
          bVal = b.uptimeSec || 0;
          break;
        default:
          return 0;
      }
      
      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      
      // Ordenação secundária por condomínio
      const aCondo = (a.superlogicaCondoName ? String(a.superlogicaCondoName) : "").toLowerCase();
      const bCondo = (b.superlogicaCondoName ? String(b.superlogicaCondoName) : "").toLowerCase();
      if (aCondo < bCondo) return -1;
      if (aCondo > bCondo) return 1;
      return 0;
    });
  }
  
  function handleSort(column) {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  }

  // ── Design tokens ──────────────────────────────────────────────────────────
  const T = {
    bg:         "#f2f1ee",
    surface:    "rgba(255,255,255,0.72)",
    surface2:   "#f9f8f6",
    ink:        "#1A1A1C",
    ink2:       "#4A4A50",
    ink3:       "#86868B",
    hair:       "rgba(20,20,22,0.07)",
    hair2:      "rgba(20,20,22,0.04)",
    accent:     "#1e3a8a",
    accentSoft: "rgba(30,58,138,0.09)",
    ok:         "#1E8E3E",
    okSoft:     "rgba(30,142,62,0.10)",
    warn:       "#B45309",
    warnSoft:   "rgba(180,83,9,0.10)",
    danger:     "#C5221F",
    dangerSoft: "rgba(197,34,31,0.10)",
    shadowSm:   "0 1px 2px rgba(20,20,22,0.04),0 1px 1px rgba(20,20,22,0.03)",
    shadowMd:   "0 6px 24px -8px rgba(20,20,22,0.08),0 2px 6px rgba(20,20,22,0.04)",
    font:       "-apple-system,BlinkMacSystemFont,'SF Pro Text',system-ui,sans-serif",
    r:          18,
  };
  const cardStyle = { background: T.surface, backdropFilter: "blur(28px) saturate(180%)", WebkitBackdropFilter: "blur(28px) saturate(180%)", border: `1px solid ${T.hair}`, borderRadius: T.r, boxShadow: T.shadowMd };

  return (
    <div style={{ fontFamily: T.font }}>
      <style>{`@keyframes sl-live{0%,100%{opacity:1}50%{opacity:.3}}@keyframes sl-fadein{from{opacity:0}to{opacity:1}}@keyframes sl-slidein{from{transform:translateX(100%)}to{transform:translateX(0)}}.layout-warning-backdrop{z-index:2000 !important;background-color:rgba(0,0,0,0.5) !important;opacity:1 !important}.layout-warning-modal{z-index:2001 !important}`}</style>

      {/* ── Page header ────────────────────────────────────────────────── */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-end", marginBottom:24 }}>
        <div>
          <h1 style={{ fontSize:28, fontWeight:600, letterSpacing:"-0.025em", color:T.ink, margin:0 }}>Telas Indoor</h1>
          <div style={{ display:"flex", alignItems:"center", gap:6, marginTop:4, fontSize:13, color:T.ink3 }}>
            <span style={{ width:6, height:6, borderRadius:"50%", background:T.ok, display:"inline-block", animation:"sl-live 2s infinite" }} />
            <span><strong style={{ color:T.ink }}>{screens.filter(s=>s.status==="online").length}</strong>{" "}de {screens.length} telas online</span>
          </div>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <button onClick={() => setShowPairModal(true)} style={{ display:"inline-flex", alignItems:"center", gap:6, padding:"8px 16px", borderRadius:10, border:"none", background:T.accent, fontSize:13, fontWeight:500, cursor:"pointer", color:"#fff", fontFamily:"inherit", boxShadow:"0 2px 8px rgba(30,58,138,0.25)" }}>
            <LinkIcon style={{ width:13, height:13 }} /> Parear tela
          </button>
        </div>
      </div>

      {/* ── Error ─────────────────────────────────────────────────────────── */}
      {error && (
        <div style={{ background:T.dangerSoft, border:`1px solid ${T.danger}`, borderRadius:12, padding:"10px 16px", marginBottom:16, color:T.danger, fontSize:13, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <span>{error}</span>
          <button onClick={() => setError("")} style={{ border:"none", background:"transparent", cursor:"pointer", color:T.danger, display:"flex" }}><XMarkIcon style={{ width:14, height:14 }} /></button>
        </div>
      )}

      {/* ── KPI alert cards ────────────────────────────────────────────────── */}
      {!loading && screens.length > 0 && (() => {
        const alerts = getAlerts();
        const kpis = [
          { key:"connection", value:alerts.connection.length, label:"Sem conexão há +4h",    tone:{ color:T.warn,    bg:T.warnSoft   }, icon:<ExclamationTriangleIcon style={{ width:14, height:14 }} />, hint:alerts.connection.length?"Verifique link da operadora":"Tudo conectado" },
          { key:"sync",       value:alerts.sync.length,       label:"Sem sincronizar há +4h", tone:{ color:T.danger,  bg:T.dangerSoft }, icon:<ArrowPathIcon style={{ width:14, height:14 }} />,           hint:alerts.sync.length?"Mídias podem estar desatualizadas":"Sincronização em dia" },
          { key:"browser",    value:alerts.browser.length,    label:"Browser inconsistente",  tone:{ color:"#6B21A8", bg:"rgba(107,33,168,0.10)" }, icon:<GlobeAltIcon style={{ width:14, height:14 }} />,  hint:alerts.browser.length?"Versão fora do padrão":"Versões compatíveis" },
          { key:"hardware",   value:alerts.hardware.length,   label:"Hardware em alerta",     tone:{ color:T.accent,  bg:T.accentSoft }, icon:<BoltIcon style={{ width:14, height:14 }} />,               hint:alerts.hardware.length?"CPU, temperatura, memória ou disco":"Hardware saudável" },
        ];
        return (
          <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:14, marginBottom:16 }}>
            {kpis.map(kpi => {
              const active = alertFilter === kpi.key;
              const hasAlert = kpi.value > 0;
              return (
                <div key={kpi.key} onClick={() => setAlertFilter(active ? null : kpi.key)} style={{ ...cardStyle, padding:"22px", borderRadius:16, boxShadow:T.shadowSm, background:"#fff", backdropFilter:"none", WebkitBackdropFilter:"none", cursor:"pointer", border:`0.5px solid ${active?kpi.tone.color:T.hair}`, transition:"border-color 0.2s" }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                    <span style={{ fontSize:12, color:T.ink3, fontWeight:500, lineHeight:1.3 }}>{kpi.label}</span>
                    <div style={{ width:28, height:28, borderRadius:8, background:kpi.tone.bg, color:kpi.tone.color, display:"grid", placeItems:"center", flexShrink:0 }}>{kpi.icon}</div>
                  </div>
                  <div style={{ fontSize:42, fontWeight:600, letterSpacing:"-0.03em", lineHeight:1, color:hasAlert?kpi.tone.color:T.ink3, margin:"14px 0 8px" }}>{kpi.value}</div>
                  <div style={{ fontSize:12, color:T.ink3 }}>
                    {kpi.hint}
                    {hasAlert && <span style={{ color:kpi.tone.color, marginLeft:6, fontWeight:500 }}>{active ? "× limpar" : "· ver →"}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* ── Filters ────────────────────────────────────────────────────────── */}
      {!loading && screens.length > 0 && (
        <div style={{ ...cardStyle, padding:"14px 16px", marginBottom:14 }}>
          <div style={{ display:"flex", alignItems:"flex-end", gap:10 }}>
            {/* Buscar */}
            <div style={{ flex:1, minWidth:240 }}>
              <div style={{ fontSize:11, fontWeight:500, color:T.ink3, marginBottom:5, letterSpacing:"0.04em", textTransform:"uppercase" }}>Buscar</div>
              <div style={{ display:"flex", alignItems:"center", gap:8, padding:"7px 12px", borderRadius:10, border:`1px solid ${T.hair}`, background:T.surface2 }}>
                <MagnifyingGlassIcon style={{ width:13, height:13, color:T.ink3, flexShrink:0 }} />
                <input
                  className="search-bare-input"
                  type="text"
                  placeholder="Nome, token, IP, cidade, condomínio…"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  style={{ flex:1, border:"none", background:"transparent", outline:"none", fontSize:13, color:T.ink, fontFamily:"inherit" }}
                />
                {searchTerm && (
                  <button onClick={() => setSearchTerm("")} style={{ border:"none", background:"transparent", color:T.ink3, cursor:"pointer", display:"flex", padding:0 }}>
                    <XMarkIcon style={{ width:13, height:13 }} />
                  </button>
                )}
              </div>
            </div>
          </div>
          <div style={{ display:"flex", gap:6, marginTop:12, flexWrap:"wrap" }}>
            {[
              { id:"all",     label:"Todas",     n:screens.length,                                          active:!statusFilter && !alertFilter },
              { id:"online",  label:"Online",    n:screens.filter(s=>s.status==="online").length,           active:statusFilter==="online" },
              { id:"offline", label:"Offline",   n:screens.filter(s=>s.status==="offline").length,          active:statusFilter==="offline" },
              { id:"alert",   label:"Em alerta", n:(() => { const a=getAlerts(); return new Set([...a.connection,...a.sync,...a.hardware,...a.browser].map(s=>s._id)).size; })(), active:alertFilter==="any-alert" },
            ].map(tab => (
              <button key={tab.id} onClick={() => {
                if (tab.id==="all")     { setStatusFilter(null); setAlertFilter(null); }
                if (tab.id==="online")  { setStatusFilter("online"); setAlertFilter(null); }
                if (tab.id==="offline") { setStatusFilter("offline"); setAlertFilter(null); }
                if (tab.id==="alert")   { setStatusFilter(null); setAlertFilter(alertFilter==="any-alert"?null:"any-alert"); }
              }} style={{ border:`1px solid ${tab.active?T.accent:T.hair}`, background:tab.active?T.accentSoft:"transparent", color:tab.active?T.accent:T.ink2, borderRadius:999, padding:"5px 12px", fontSize:12, fontWeight:500, cursor:"pointer", display:"inline-flex", gap:6, alignItems:"center", fontFamily:"inherit" }}>
                {tab.label}
                <span style={{ color:T.ink3, fontVariantNumeric:"tabular-nums" }}>{tab.n}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Table card ─────────────────────────────────────────────────────── */}
      <div style={{ ...cardStyle, marginBottom:14 }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"16px 20px", borderBottom:`1px solid ${T.hair}` }}>
          <h3 style={{ fontSize:13, fontWeight:600, letterSpacing:"-0.005em", color:T.ink, margin:0, display:"flex", alignItems:"center", gap:8 }}>
            Frota de telas
            <span style={{ fontWeight:400, color:T.ink3 }}>· {loading?"…":`${getFilteredScreens().length} de ${screens.length}`}</span>
          </h3>
          <div style={{ display:"inline-flex", background:"#EFECE5", borderRadius:8, padding:3, gap:2 }}>
            {[{v:"list",l:"≡"},{v:"grid",l:"⊞"}].map(o => (
              <button key={o.v} onClick={() => setView(o.v)} style={{ border:0, fontSize:13, fontWeight:600, padding:"4px 10px", borderRadius:6, cursor:"pointer", fontFamily:"inherit", background:view===o.v?T.surface:"transparent", color:view===o.v?T.ink:T.ink3, boxShadow:view===o.v?"0 1px 2px rgba(0,0,0,.05)":"none" }}>{o.l}</button>
            ))}
          </div>
        </div>
        {loading ? (
          <div style={{ textAlign:"center", padding:"60px 20px", color:T.ink3 }}>
            <Spinner animation="border" size="sm" />
            <div style={{ marginTop:8, fontSize:13 }}>Carregando telas…</div>
          </div>
        ) : screens.length === 0 ? (
          <div style={{ padding:"60px 20px", textAlign:"center", color:T.ink3, fontSize:13 }}>Nenhuma tela cadastrada</div>
        ) : getFilteredScreens().length === 0 ? (
          <div style={{ padding:"60px 20px", textAlign:"center", color:T.ink3, fontSize:13 }}>Nenhuma tela encontrada com os filtros aplicados</div>
        ) : view === "list" ? (
          <div style={{ overflowX:"auto" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
              <thead>
                <tr>
                  {[["status","Status"],["name","Nome"],["condo","Condomínio"],["city","Cidade/UF"]].map(([k,l]) => (
                    <th key={k} onClick={() => handleSort(k)} style={{ textAlign:"left", fontWeight:500, fontSize:11, color:T.ink3, letterSpacing:"0.04em", textTransform:"uppercase", padding:"12px 16px", borderBottom:`1px solid ${T.hair}`, background:"transparent", cursor:"pointer", userSelect:"none", whiteSpace:"nowrap" }}>
                      {l}{sortColumn===k && <span style={{ marginLeft:3 }}>{sortDirection==="asc"?"↑":"↓"}</span>}
                    </th>
                  ))}
                  <th style={{ textAlign:"left", fontWeight:500, fontSize:11, color:T.ink3, letterSpacing:"0.04em", textTransform:"uppercase", padding:"12px 16px", borderBottom:`1px solid ${T.hair}`, background:"transparent", whiteSpace:"nowrap" }}>Saúde · Hardware</th>
                  <th style={{ textAlign:"left", fontWeight:500, fontSize:11, color:T.ink3, letterSpacing:"0.04em", textTransform:"uppercase", padding:"12px 16px", borderBottom:`1px solid ${T.hair}`, background:"transparent", whiteSpace:"nowrap" }}>Conexão · Sinc.</th>
                  <th onClick={() => handleSort("uptime")} style={{ textAlign:"left", fontWeight:500, fontSize:11, color:T.ink3, letterSpacing:"0.04em", textTransform:"uppercase", padding:"12px 16px", borderBottom:`1px solid ${T.hair}`, background:"transparent", cursor:"pointer", userSelect:"none", whiteSpace:"nowrap" }}>
                    Uptime{sortColumn==="uptime" && <span style={{ marginLeft:3 }}>{sortDirection==="asc"?"↑":"↓"}</span>}
                  </th>
                  <th style={{ textAlign:"right", fontWeight:500, fontSize:11, color:T.ink3, letterSpacing:"0.04em", textTransform:"uppercase", padding:"12px 20px 12px 16px", borderBottom:`1px solid ${T.hair}`, background:"transparent" }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {getFilteredScreens().map(screen => (
                  <tr key={screen._id} style={{ borderBottom:`1px solid ${T.hair2}` }}>
                    <td style={{ padding:"14px 16px", verticalAlign:"middle" }}>
                      <span style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"3px 10px", fontSize:11.5, fontWeight:500, borderRadius:999, background:screen.status==="online"?T.okSoft:"rgba(20,20,22,0.06)", color:screen.status==="online"?T.ok:T.ink3 }}>
                        <span style={{ width:5, height:5, borderRadius:"50%", background:screen.status==="online"?T.ok:T.ink3, display:"inline-block" }} />
                        {screen.status==="online"?"Online":"Offline"}
                      </span>
                      {screen.status==="online" && screen.browserRunning===false && (
                        <div style={{ marginTop:4 }}>
                          <span style={{ display:"inline-flex", alignItems:"center", gap:4, padding:"2px 8px", fontSize:11, fontWeight:500, borderRadius:999, background:T.dangerSoft, color:T.danger }}>Browser off</span>
                        </div>
                      )}
                    </td>
                    <td style={{ padding:"14px 16px", verticalAlign:"middle" }}>
                      <div style={{ fontWeight:500, color:T.ink }}>{screen.name || <em style={{ color:T.ink3, fontStyle:"normal" }}>(sem nome)</em>}</div>
                      <div style={{ fontSize:11.5, color:T.ink3, marginTop:1 }}>{layoutTypeLabel(screen.layoutType)}</div>
                    </td>
                    <td style={{ padding:"14px 16px", verticalAlign:"middle", color:T.ink2 }}>{screen.superlogicaCondoName || "—"}</td>
                    <td style={{ padding:"14px 16px", verticalAlign:"middle", color:T.ink2 }}>{screen.city||"—"}{screen.uf?`/${screen.uf}`:""}</td>
                    {/* Saúde · Hardware — medidores inline (CPU, RAM, Disco, Temp) */}
                    <td style={{ padding:"14px 16px", verticalAlign:"middle", whiteSpace:"nowrap" }}>
                      {screen.status !== "online" ? (
                        <span style={{ color:T.ink3, fontSize:12.5, opacity:0.6 }}>— sem dados (offline)</span>
                      ) : (() => {
                        const ramPct = screen.memoryUsedMb != null && screen.memoryTotalMb ? Math.round((screen.memoryUsedMb / screen.memoryTotalMb) * 100) : null;
                        const diskPct = screen.diskFreeGb != null && screen.diskTotalGb ? Math.round(((screen.diskTotalGb - screen.diskFreeGb) / screen.diskTotalGb) * 100) : null;
                        const cpu = screen.cpuUsagePct;
                        const temp = screen.cpuTemp;
                        const colorOf = (st) => st === "crit" ? T.danger : st === "warn" ? T.warn : T.ink2;
                        const gauges = [
                          { lbl:"CPU",   state: cpu != null ? cpuStateOf(cpu) : "ok",       val: cpu != null ? <>{cpu.toFixed(1)}<span style={{ fontSize:9, color:T.ink3, marginLeft:1 }}>%</span></> : "—" },
                          { lbl:"RAM",   state: ramPct != null ? ramStateOf(ramPct) : "ok",  val: ramPct != null ? <>{ramPct}<span style={{ fontSize:9, color:T.ink3, marginLeft:1 }}>%</span></> : "—" },
                          { lbl:"Disco", state: diskPct != null ? diskStateOf(diskPct) : "ok", val: diskPct != null ? <>{diskPct}<span style={{ fontSize:9, color:T.ink3, marginLeft:1 }}>%</span></> : "—" },
                          { lbl:"Temp",  state: temp != null ? tempStateOf(temp) : "ok",     val: temp != null ? <>{Math.round(temp)}<span style={{ fontSize:9, color:T.ink3, marginLeft:1 }}>°C</span></> : "—" },
                        ];
                        return (
                          <div style={{ display:"flex", alignItems:"center", gap:16 }}>
                            {gauges.map((g) => (
                              <div key={g.lbl} style={{ display:"flex", flexDirection:"column", gap:2, minWidth:46 }}>
                                <span style={{ fontSize:9.5, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.04em", color:T.ink3 }}>{g.lbl}</span>
                                <span style={{ fontSize:13, fontVariantNumeric:"tabular-nums", color:colorOf(g.state), fontWeight:g.state==="ok"?400:600, display:"inline-flex", alignItems:"center", gap:4 }}>
                                  {g.val}
                                  {g.state!=="ok" && <span style={{ width:5, height:5, borderRadius:"50%", background:"currentColor", display:"inline-block" }} />}
                                </span>
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </td>
                    {/* Conexão · Sinc. */}
                    <td style={{ padding:"14px 16px", verticalAlign:"middle", whiteSpace:"nowrap", fontVariantNumeric:"tabular-nums" }}>
                      {(() => {
                        const syncStale = screen.lastSyncAt ? ((Date.now() - new Date(screen.lastSyncAt).getTime()) / 36e5) > 4 : false;
                        return (
                          <>
                            <div style={{ display:"flex", alignItems:"baseline", gap:7 }}>
                              <span style={{ width:34, flexShrink:0, fontSize:9.5, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.04em", color:T.ink3 }}>Conex.</span>
                              <span style={{ fontSize:12.5, color:T.ink2 }}>{formatShortDate(screen.lastSeenAt)}</span>
                            </div>
                            <div style={{ display:"flex", alignItems:"baseline", gap:7, marginTop:3 }}>
                              <span style={{ width:34, flexShrink:0, fontSize:9.5, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.04em", color:T.ink3 }}>Sinc.</span>
                              <span style={{ fontSize:12, color:syncStale?T.warn:T.ink3, fontWeight:syncStale?600:400 }}>{formatShortDate(screen.lastSyncAt)}</span>
                            </div>
                          </>
                        );
                      })()}
                    </td>
                    <td style={{ padding:"14px 16px", verticalAlign:"middle", color:T.ink2, fontVariantNumeric:"tabular-nums" }}>{formatUptime(screen.uptimeSec)}</td>
                    <td style={{ padding:"14px 20px 14px 16px", verticalAlign:"middle", textAlign:"right" }}>
                      <div style={{ display:"flex", gap:4, justifyContent:"flex-end" }}>
                        <button onClick={() => openFooterModal(screen)} title="Configurar barra" style={{ width:28, height:28, border:`1px solid ${T.hair}`, borderRadius:8, background:"rgba(107,33,168,0.08)", color:"#6B21A8", cursor:"pointer", display:"grid", placeItems:"center" }}>
                          <SwatchIcon style={{ width:14, height:14 }} />
                        </button>
                        <button onClick={() => openHealthModal(screen)} title="Saúde do sistema" style={{ width:28, height:28, border:`1px solid ${T.hair}`, borderRadius:8, background:T.okSoft, color:T.ok, cursor:"pointer", display:"grid", placeItems:"center" }}>
                          <WrenchScrewdriverIcon style={{ width:14, height:14 }} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ padding:"18px", display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(290px,1fr))", gap:14 }}>
            {getFilteredScreens().map(screen => {
              const syncStale = screen.lastSyncAt ? ((Date.now() - new Date(screen.lastSyncAt).getTime()) / 36e5) > 4 : false;
              const ramPct = screen.memoryUsedMb != null && screen.memoryTotalMb ? Math.round((screen.memoryUsedMb / screen.memoryTotalMb) * 100) : null;
              const diskPct = screen.diskFreeGb != null && screen.diskTotalGb ? Math.round(((screen.diskTotalGb - screen.diskFreeGb) / screen.diskTotalGb) * 100) : null;
              const cpu = screen.cpuUsagePct;
              const temp = screen.cpuTemp;
              const colorOf = (st) => st === "crit" ? T.danger : st === "warn" ? T.warn : T.ink2;
              const gauges = [
                { lbl:"CPU",   state: cpu != null ? cpuStateOf(cpu) : "ok",          val: cpu != null ? `${cpu.toFixed(1)}%` : "—" },
                { lbl:"RAM",   state: ramPct != null ? ramStateOf(ramPct) : "ok",    val: ramPct != null ? `${ramPct}%` : "—" },
                { lbl:"Disco", state: diskPct != null ? diskStateOf(diskPct) : "ok", val: diskPct != null ? `${diskPct}%` : "—" },
                { lbl:"Temp",  state: temp != null ? tempStateOf(temp) : "ok",        val: temp != null ? `${Math.round(temp)}°C` : "—" },
              ];
              return (
                <div key={screen._id} onClick={() => openHealthModal(screen)}
                  style={{ border:`0.5px solid ${T.hair}`, borderRadius:T.r, padding:18, background:T.surface, cursor:"pointer", display:"flex", flexDirection:"column", gap:12, boxShadow:T.shadowSm }}>

                  {/* sc-top: nome + status */}
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:8 }}>
                    <span style={{ fontWeight:600, fontSize:15.5, letterSpacing:"-0.01em", color:T.ink, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{screen.name||"(sem nome)"}</span>
                    <span style={{ display:"inline-flex", alignItems:"center", gap:7, padding:"4px 10px", fontSize:12, fontWeight:600, borderRadius:999, background:screen.status==="online"?T.okSoft:"rgba(20,20,22,0.06)", color:screen.status==="online"?T.ok:T.ink3, flexShrink:0 }}>
                      <span style={{ width:7, height:7, borderRadius:"50%", background:"currentColor" }} />
                      {screen.status==="online"?"Online":"Offline"}
                    </span>
                  </div>

                  {/* sc-cond + sc-city */}
                  <div>
                    <div style={{ fontSize:13.5, color:T.ink }}>{screen.superlogicaCondoName||"—"}</div>
                    <div style={{ fontSize:12.5, color:T.ink3, marginTop:2 }}>{screen.city||"—"}{screen.uf?`/${screen.uf}`:""} · {layoutTypeLabel(screen.layoutType)}</div>
                  </div>

                  {/* sc-date: Conexão · Sinc. */}
                  <div style={{ display:"flex", flexWrap:"wrap", gap:"4px 16px", fontVariantNumeric:"tabular-nums" }}>
                    <div style={{ display:"flex", alignItems:"baseline", gap:7 }}>
                      <span style={{ width:34, flexShrink:0, fontSize:9.5, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.04em", color:T.ink3 }}>Conex.</span>
                      <span style={{ fontSize:12.5, color:T.ink2 }}>{formatShortDate(screen.lastSeenAt)}</span>
                    </div>
                    <div style={{ display:"flex", alignItems:"baseline", gap:7 }}>
                      <span style={{ width:34, flexShrink:0, fontSize:9.5, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.04em", color:T.ink3 }}>Sinc.</span>
                      <span style={{ fontSize:12, color:syncStale?T.warn:T.ink3, fontWeight:syncStale?600:400 }}>{formatShortDate(screen.lastSyncAt)}</span>
                    </div>
                  </div>

                  {/* sc-health: separador + medidores */}
                  <div style={{ display:"flex", gap:18, paddingTop:12, borderTop:`0.5px solid ${T.hair}` }}>
                    {screen.status !== "online" ? (
                      <span style={{ fontSize:12.5, color:T.ink3, opacity:0.6 }}>— sem dados (offline)</span>
                    ) : (
                      gauges.map((g) => (
                        <div key={g.lbl} style={{ display:"flex", flexDirection:"column", gap:4, minWidth:52 }}>
                          <span style={{ fontSize:9.5, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.04em", color:T.ink3 }}>{g.lbl}</span>
                          <span style={{ fontSize:13, fontVariantNumeric:"tabular-nums", color:colorOf(g.state), fontWeight:g.state==="ok"?400:600, display:"inline-flex", alignItems:"center", gap:4 }}>
                            {g.val}
                            {g.state!=="ok" && <span style={{ width:5, height:5, borderRadius:"50%", background:"currentColor", display:"inline-block", flexShrink:0 }} />}
                          </span>
                        </div>
                      ))
                    )}
                  </div>

                  {/* sc-actions */}
                  <div style={{ display:"flex", justifyContent:"flex-end", gap:6 }} onClick={e => e.stopPropagation()}>
                    <button onClick={() => openFooterModal(screen)} title="Configurar barra" style={{ width:34, height:34, border:"none", borderRadius:10, background:"rgba(107,33,168,0.08)", color:"#6B21A8", cursor:"pointer", display:"grid", placeItems:"center" }}>
                      <SwatchIcon style={{ width:17, height:17 }} />
                    </button>
                    <button onClick={() => openHealthModal(screen)} title="Saúde do sistema" style={{ width:34, height:34, border:"none", borderRadius:10, background:T.okSoft, color:T.ok, cursor:"pointer", display:"grid", placeItems:"center" }}>
                      <WrenchScrewdriverIcon style={{ width:17, height:17 }} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal de Pareamento por Código */}
      <Modal show={showPairModal} onHide={() => { resetPairModal(); setShowPairModal(false); }} centered>
        <Modal.Header closeButton style={{ borderBottom: '1px solid #e9ecef' }}>
          <Modal.Title><LinkIcon style={{ width: 18, height: 18, marginRight: 8, verticalAlign: 'middle' }} />Parear Nova Tela</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info" style={{ borderRadius: '10px', fontSize: '0.9rem' }}>
            <strong>Como parear:</strong><br />
            1. Ligue o dispositivo (RPi ou Android) sem token configurado<br />
            2. Ele exibirá um <strong>código de 6 dígitos</strong> na tela<br />
            3. Digite o código abaixo e dê um nome para a tela
          </Alert>

          <Form.Group className="mb-3">
            <Form.Label><strong>Código do Dispositivo</strong></Form.Label>
            <Form.Control
              type="text"
              placeholder="Ex: 482931"
              value={pairCode}
              maxLength={6}
              onChange={(e) => setPairCode(autoFormatPairCode(e.target.value))}
              style={{
                fontSize: '1.6rem',
                letterSpacing: '0.2rem',
                textAlign: 'center',
                fontWeight: '700',
                fontFamily: 'monospace',
                borderRadius: '12px',
                border: '2px solid #007aff'
              }}
            />
            <Form.Text className="text-muted">6 dígitos numéricos (exibido no dispositivo)</Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label><strong>Nome da Tela</strong></Form.Label>
            <Form.Control
              type="text"
              placeholder="Ex: TV Lobby Bloco A"
              value={pairName}
              onChange={(e) => setPairName(e.target.value)}
              style={{ borderRadius: '10px' }}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label><strong>Tipo de Localização</strong></Form.Label>
            <Form.Select
              value={pairLocationType}
              onChange={(e) => { setPairLocationType(e.target.value); setPairSelectedCondo(null); setPairCondoSearchTerm(""); setPairCondos([]); }}
              style={{ borderRadius: '10px' }}
            >
              <option value="AVULSO">Avulso</option>
              <option value="CONDOMINIO">Condomínio</option>
            </Form.Select>
          </Form.Group>

          {pairLocationType === "CONDOMINIO" && (
            <Form.Group className="mb-3">
              <Form.Label><strong>Condomínio <span style={{color:'#dc3545'}}>*</span></strong></Form.Label>
              <div style={{ position: 'relative' }}>
                <Form.Control
                  type="text"
                  placeholder="Digite o nome do condomínio..."
                  value={pairCondoSearchTerm}
                  onChange={(e) => handlePairCondoSearchChange(e.target.value)}
                  autoComplete="off"
                  style={{ borderRadius: '10px' }}
                />
                {(pairCondos.length > 0 || pairSearchingCondos) && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0,
                    maxHeight: 240, overflowY: 'auto',
                    border: '1px solid #dee2e6', borderRadius: '8px',
                    backgroundColor: 'white', zIndex: 1050,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.12)', marginTop: 2
                  }}>
                    {pairSearchingCondos && (
                      <div style={{ padding: '1rem', textAlign: 'center' }}>
                        <Spinner animation="border" size="sm" />
                      </div>
                    )}
                    {!pairSearchingCondos && pairCondos.map((condo, idx) => (
                      <div key={idx} onClick={() => handleSelectPairCondo(condo)}
                        style={{
                          padding: '0.7rem 1rem',
                          borderBottom: idx < pairCondos.length - 1 ? '1px solid #f0f0f0' : 'none',
                          cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                      >
                        <div style={{ fontWeight: 600 }}>{condo.name}</div>
                        <div style={{ fontSize: '0.82rem', color: '#86868b' }}>{condo.address} — {condo.city}/{condo.uf}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {!pairSearchingCondos && pairCondoSearchTerm && pairCondos.length === 0 && (
                <small style={{ color: '#d32f2f', marginTop: 4, display: 'block' }}>Nenhum condomínio encontrado.</small>
              )}
              {pairSelectedCondo && (
                <div style={{ marginTop: 8, padding: '0.6rem 0.9rem', backgroundColor: '#e7f3ff', borderRadius: 8, border: '1px solid #b3e5fc' }}>
                  <small style={{ color: '#555' }}>✓ Selecionado:</small>
                  <div style={{ fontWeight: 600 }}>{pairSelectedCondo.name}</div>
                  <div style={{ fontSize: '0.82rem', color: '#666' }}>{pairSelectedCondo.address} — {pairSelectedCondo.city}/{pairSelectedCondo.uf}</div>
                  <Button variant="link" size="sm" style={{ padding: 0, fontSize: '0.8rem', color: '#dc3545' }}
                    onClick={() => setPairSelectedCondo(null)}>
                    Trocar condomínio
                  </Button>
                </div>
              )}
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { resetPairModal(); setShowPairModal(false); }} disabled={clainingPair}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleClaimPairing}
            disabled={clainingPair || !/^\d{6}$/.test(pairCode.trim()) || !pairName.trim() || (pairLocationType === "CONDOMINIO" && !pairSelectedCondo)}
            style={{ borderRadius: '10px', minWidth: '120px' }}
          >
            {clainingPair ? <><Spinner size="sm" className="me-2" />Pareando...</> : <><LinkIcon style={{ width: 15, height: 15 }} className="me-1" />Parear</>}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de Editar Nome */}
      <Modal show={showEditNameModal} onHide={() => setShowEditNameModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Editar Nome da Tela</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingScreen && (
            <>
              <Alert variant="info">
                <strong>Token:</strong> {editingScreen.token}
              </Alert>

              <Form.Group className="mb-3">
                <Form.Label>Nome da Tela</Form.Label>
                <Form.Control
                  type="text"
                  value={editingName}
                  onChange={(e) => setEditingName(e.target.value)}
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditNameModal(false)}>
            Cancelar
          </Button>
          <Button 
            variant="primary"
            onClick={handleSaveEditName}
            disabled={savingEditName}
          >
            {savingEditName ? <Spinner size="sm" className="me-2" /> : ""} Salvar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de Associação de Condomínio */}
      <Modal show={showModal} onHide={closeModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Associar Condomínio</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedScreen && (
            <>
              <Alert variant="info">
                <strong>Tela:</strong> {selectedScreen.name || "(sem nome)"}
                <br />
                <strong>Token:</strong> {selectedScreen.token}
              </Alert>

              <Form.Group className="mb-3">
                <Form.Label>Selecione o Condomínio</Form.Label>
                <div style={{ position: 'relative' }}>
                  <Form.Control
                    type="text"
                    placeholder="Digite o nome do condomínio..."
                    value={condoSearchTerm}
                    onChange={(e) => handleCondoSearchChange(e.target.value)}
                    autoComplete="off"
                    style={{
                      borderRadius: '10px',
                      border: '1px solid #d1d1d6',
                      padding: '0.6rem 1rem',
                      fontSize: '0.95rem',
                      transition: 'all 0.2s'
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = '#007aff';
                      e.currentTarget.style.boxShadow = '0 0 0 3px rgba(0, 122, 255, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = '#d1d1d6';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  />

                  {/* Dropdown de Autocomplete */}
                  {(condos.length > 0 || searchingCondos) && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        maxHeight: 300,
                        overflowY: 'auto',
                        border: '1px solid #dee2e6',
                        borderRadius: '8px',
                        borderTop: 'none',
                        backgroundColor: 'white',
                        zIndex: 1000,
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                        marginTop: '-1px'
                      }}
                    >
                      {searchingCondos && (
                        <div style={{ padding: '1rem', textAlign: 'center' }}>
                          <Spinner animation="border" size="sm" />
                          <p style={{ marginTop: '0.5rem', color: '#6c757d', fontSize: '0.9rem' }}>
                            Buscando...
                          </p>
                        </div>
                      )}

                      {!searchingCondos && condos.map((condo, idx) => (
                        <div
                          key={idx}
                          onClick={() => handleSelectCondo(condo)}
                          style={{
                            padding: '0.75rem 1rem',
                            borderBottom: idx < condos.length - 1 ? '1px solid #f0f0f0' : 'none',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s',
                            backgroundColor: selectedCondo === condo ? '#e7f3ff' : 'white'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#f8f9fa';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = selectedCondo === condo ? '#e7f3ff' : 'white';
                          }}
                        >
                          <div style={{ fontWeight: '600', color: '#1d1d1f' }}>
                            {condo.name}
                          </div>
                          <div style={{ fontSize: '0.85rem', color: '#86868b', marginTop: '0.25rem' }}>
                            {condo.address} - {condo.city}/{condo.uf}
                          </div>
                          {condo.cep && (
                            <div style={{ fontSize: '0.8rem', color: '#a1a1a6', marginTop: '0.25rem' }}>
                              CEP: {condo.cep}
                            </div>
                          )}
                          {condo.id && (
                            <Badge bg="secondary" style={{ marginTop: '0.25rem' }}>
                              ID: {condo.id}
                            </Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                {!searchingCondos && condoSearchTerm && condos.length === 0 && (
                  <small style={{ color: '#d32f2f', marginTop: '0.5rem', display: 'block' }}>
                    Nenhum condomínio encontrado.
                  </small>
                )}

                {selectedCondo && (
                  <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: '#e7f3ff', borderRadius: '8px', border: '1px solid #b3e5fc' }}>
                    <div style={{ fontSize: '0.9rem', color: '#666' }}>
                      ✓ Condomínio selecionado:
                    </div>
                    <div style={{ fontWeight: '600', marginTop: '0.5rem', color: '#1d1d1f' }}>
                      {selectedCondo.name}
                    </div>
                    {selectedCondo.address && (
                      <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>
                        {selectedCondo.address} - {selectedCondo.city}/{selectedCondo.uf}
                      </div>
                    )}
                  </div>
                )}
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer style={{ 
          borderTop: '1px solid #e5e5e7',
          padding: '1.25rem 1.5rem',
          backgroundColor: '#fafafa'
        }}>
          <Button 
            onClick={closeModal}
            style={{
              background: 'white',
              color: '#1d1d1f',
              border: '1px solid #d1d1d6',
              borderRadius: '10px',
              padding: '0.6rem 1.5rem',
              fontWeight: '600',
              fontSize: '0.95rem',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f5f5f7';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'white';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleBindCondo}
            disabled={!selectedCondo || saving}
            style={{
              background: !selectedCondo || saving ? '#d1d1d6' : '#007aff',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              padding: '0.6rem 1.5rem',
              fontWeight: '600',
              fontSize: '0.95rem',
              boxShadow: !selectedCondo || saving ? 'none' : '0 2px 8px rgba(0, 122, 255, 0.3)',
              transition: 'all 0.2s',
              opacity: !selectedCondo || saving ? 0.5 : 1
            }}
            onMouseEnter={(e) => {
              if (!(!selectedCondo || saving)) {
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 122, 255, 0.4)';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }
            }}
            onMouseLeave={(e) => {
              if (!(!selectedCondo || saving)) {
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 122, 255, 0.3)';
                e.currentTarget.style.transform = 'translateY(0)';
              }
            }}
          >
            {saving ? (
              <>
                <Spinner animation="border" size="sm" /> Salvando...
              </>
            ) : (
              "Vincular Condomínio"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de Barra Inferior */}
      {showFooterModal && footerScreen && (
        <>
          <div onClick={closeFooterModal} style={{ position:"fixed", inset:0, background:"rgba(20,20,22,0.45)", backdropFilter:"blur(3px)", WebkitBackdropFilter:"blur(3px)", zIndex:1060, animation:"sl-fadein 0.2s ease" }} />
          <div onClick={e => e.stopPropagation()} style={{ position:"fixed", top:"50%", left:"50%", transform:"translate(-50%,-50%)", width:"min(680px,96vw)", maxHeight:"90vh", background:"#fff", borderRadius:18, boxShadow:"0 24px 80px rgba(20,20,22,0.22)", zIndex:1061, display:"flex", flexDirection:"column", fontFamily:"-apple-system,BlinkMacSystemFont,'SF Pro Text',system-ui,sans-serif", animation:"sl-fadein 0.2s ease" }}>

            {/* Header */}
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"1.125rem 1.5rem", borderBottom:"0.5px solid rgba(20,20,22,0.07)", flexShrink:0 }}>
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <span style={{ width:28, height:28, borderRadius:8, background:"rgba(107,33,168,0.10)", color:"#6B21A8", display:"grid", placeItems:"center", flexShrink:0 }}>
                  <SwatchIcon style={{ width:14, height:14 }} />
                </span>
                <h3 style={{ margin:0, fontSize:15, fontWeight:600, letterSpacing:"-0.01em", color:"#1A1A1C" }}>Configurar barra inferior</h3>
              </div>
              <button onClick={closeFooterModal} style={{ width:30, height:30, border:"1px solid rgba(20,20,22,0.07)", borderRadius:8, background:"rgba(20,20,22,0.04)", color:"#1A1A1C", cursor:"pointer", display:"grid", placeItems:"center", flexShrink:0 }}>
                <XMarkIcon style={{ width:15, height:15 }} />
              </button>
            </div>

            {/* Body */}
            <div style={{ overflowY:"auto", flex:1, padding:"1.5rem", display:"flex", flexDirection:"column", gap:22 }}>

              {/* Imagem da barra */}
              <div>
                <div style={{ fontSize:13, fontWeight:600, marginBottom:8, color:"#1A1A1C" }}>Imagem da barra</div>
                <div style={{ position:"relative" }}>
                  {/* Trigger — exibe item selecionado, clique abre dropdown */}
                  <div onClick={() => !savingFooter && setFooterDropdownOpen(o => !o)}
                    style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"10px 12px", borderRadius:10, border:`1px solid ${footerDropdownOpen ? "#1e3a8a" : "rgba(20,20,22,0.12)"}`, background:"rgba(20,20,22,0.03)", fontSize:13, color: footerMediaAssetId ? "#1A1A1C" : "#86868B", cursor: savingFooter ? "not-allowed" : "pointer", userSelect:"none" }}>
                    <span style={{ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                      {footerMediaAssetId
                        ? (() => { const s = footerMediaAssets.find(m => m._id === footerMediaAssetId); return s ? `${s.name || "(sem nome)"} — ${s.partnerId?.company || "(sem parceiro)"}` : "Sem barra"; })()
                        : "Sem barra"}
                    </span>
                    <span style={{ fontSize:11, color:"#86868B", flexShrink:0, marginLeft:8 }}>{footerDropdownOpen ? "▲" : "▼"}</span>
                  </div>
                  {/* Dropdown */}
                  {footerDropdownOpen && (
                    <div style={{ position:"absolute", top:"calc(100% + 4px)", left:0, right:0, zIndex:200, background:"#fff", border:"1px solid rgba(20,20,22,0.12)", borderRadius:10, boxShadow:"0 8px 24px rgba(20,20,22,0.12)", display:"flex", flexDirection:"column" }}>
                      <div style={{ padding:"8px 8px 4px" }}>
                        <input autoFocus type="text" placeholder="Pesquisar barra…" value={footerMediaSearch}
                          onChange={e => setFooterMediaSearch(e.target.value)}
                          style={{ width:"100%", boxSizing:"border-box", padding:"7px 10px", borderRadius:8, border:"1px solid rgba(20,20,22,0.12)", background:"rgba(20,20,22,0.04)", fontSize:12, color:"#1A1A1C", fontFamily:"inherit", outline:"none" }}
                        />
                      </div>
                      <div style={{ maxHeight:200, overflowY:"auto" }}>
                        {[{ _id:"", name:"Sem barra", partnerId:null },
                          ...footerMediaAssets.filter(m => {
                            const q = footerMediaSearch.toLowerCase();
                            return !q || (m.name||"").toLowerCase().includes(q) || (m.partnerId?.company||"").toLowerCase().includes(q);
                          })
                        ].map((m, i, arr) => (
                          <div key={m._id}
                            onClick={() => { setFooterMediaAssetId(m._id); setFooterDropdownOpen(false); setFooterMediaSearch(""); }}
                            style={{ padding:"8px 12px", fontSize:13, cursor:"pointer", background: footerMediaAssetId === m._id ? "rgba(30,58,138,0.08)" : "transparent", color: footerMediaAssetId === m._id ? "#1e3a8a" : m._id === "" ? "#86868B" : "#1A1A1C", fontWeight: footerMediaAssetId === m._id ? 600 : 400, borderBottom: i < arr.length - 1 ? "0.5px solid rgba(20,20,22,0.05)" : "none" }}>
                            {m._id === "" ? "Sem barra" : `${m.name || "(sem nome)"} — ${m.partnerId?.company || "(sem parceiro)"}`}
                          </div>
                        ))}
                        {footerMediaAssets.filter(m => { const q = footerMediaSearch.toLowerCase(); return !q || (m.name||"").toLowerCase().includes(q) || (m.partnerId?.company||"").toLowerCase().includes(q); }).length === 0 && footerMediaSearch && (
                          <div style={{ padding:"10px 12px", fontSize:13, color:"#86868B", textAlign:"center" }}>Nenhuma barra encontrada</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <p style={{ fontSize:12, color:"#86868B", margin:"6px 0 0" }}>
                  Altura: <b style={{ color:"#4A4A50" }}>6,25% da tela</b> (120px em 1080×1920) · escala proporcionalmente em qualquer resolução.
                </p>
                {/* Preview da barra */}
                <div style={{ marginTop:12, border:"0.5px solid rgba(20,20,22,0.07)", borderRadius:12, padding:14, background:"rgba(20,20,22,0.03)" }}>
                  <div style={{ fontSize:11, color:"#86868B", textTransform:"uppercase", letterSpacing:"0.04em", marginBottom:8 }}>Preview da barra</div>
                  {(() => {
                    const sel = footerMediaAssets.find(m => m._id === footerMediaAssetId);
                    const url = sel?.publicUrl || sel?.cdnUrl || sel?.url;
                    return url ? (
                      <div style={{ width:"100%", height:50, borderRadius:8, overflow:"hidden", boxShadow:"0 2px 8px rgba(0,0,0,0.1)" }}>
                        <img src={url} alt="Preview" style={{ width:"100%", height:"100%", objectFit:"contain", display:"block" }} />
                      </div>
                    ) : (
                      <div style={{ width:"100%", height:50, background:"#1f3a7a", borderRadius:8, display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
                        <span style={{ height:"65%", aspectRatio:"1", borderRadius:3, background:"white", color:"#1f3a7a", display:"inline-flex", alignItems:"center", justifyContent:"center", fontWeight:700, fontSize:11 }}>P</span>
                        <span style={{ color:"white", fontSize:12, fontWeight:600, letterSpacing:"0.04em" }}>
                          {footerMediaAssetId ? "LOGO DO CLIENTE | tagline / parceiro" : "Sem barra selecionada"}
                        </span>
                      </div>
                    );
                  })()}
                  <div style={{ textAlign:"center", marginTop:8, fontSize:11.5, color:"#86868B" }}>
                    {(() => { const sel = footerMediaAssets.find(m => m._id === footerMediaAssetId); return sel ? `${sel.name || "(sem nome)"} · 6,25% da altura da tela` : "Nenhuma barra selecionada"; })()}
                  </div>
                </div>
              </div>

              {/* Layout da tela */}
              <div>
                <div style={{ fontSize:13, fontWeight:600, marginBottom:4, color:"#1A1A1C" }}>Layout da tela</div>
                <p style={{ fontSize:12, color:"#86868B", margin:"0 0 12px" }}>
                  <b style={{ color:"#4A4A50" }}>{layoutType === "layout4" ? "Retrato comunicados" : "Retrato padrão"}:</b>{" "}
                  {layoutType === "layout4" ? "comunicados ocupam toda a tela, sem espaço para mídia. Rodapé continua disponível." : "mídia ao topo, comunicados empilhados abaixo. Layout original."}
                </p>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
                  {[
                    { key:"layout2", label:"Retrato padrão",       variant:"padrao" },
                    { key:"layout4", label:"Retrato comunicados",   variant:"comunicados" },
                  ].map(opt => (
                    <div key={opt.key} onClick={() => footerScope === "single" && setLayoutType(opt.key)}
                      style={{ padding:12, borderRadius:14, border: layoutType === opt.key ? "2px solid #1e3a8a" : "1.5px solid rgba(20,20,22,0.09)", background: layoutType === opt.key ? "rgba(30,58,138,0.06)" : "white", cursor: footerScope === "single" ? "pointer" : "not-allowed", display:"flex", flexDirection:"column", alignItems:"center", gap:10, opacity: footerScope === "single" ? 1 : 0.5 }}>
                      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", width:"100%" }}>
                        <span style={{ fontSize:12, fontWeight:600, color: layoutType === opt.key ? "#1e3a8a" : "#4A4A50" }}>{opt.label}</span>
                        {layoutType === opt.key && (
                          <span style={{ width:18, height:18, borderRadius:"50%", background:"#1e3a8a", display:"grid", placeItems:"center" }}>
                            <CheckIcon style={{ width:11, height:11, color:"white" }} />
                          </span>
                        )}
                      </div>
                      {/* Miniatura do mock 9:16 */}
                      <div style={{ width:80, aspectRatio:"9/16", background:"#1A1A1C", borderRadius:9, padding:"1.5%", boxShadow:"0 4px 12px rgba(0,0,0,0.2)" }}>
                        <div style={{ width:"100%", height:"100%", borderRadius:7, background:"white", overflow:"hidden", display:"flex", flexDirection:"column" }}>
                          {opt.variant === "padrao" ? (
                            <>
                              <div style={{ height:"56.25%", background:"linear-gradient(135deg,#1e3a8a,#7c3aed)", flexShrink:0 }} />
                              <div style={{ height:"37.5%", background:"white", padding:"4px 5px", borderTop:"0.5px solid #00000018", display:"flex", flexDirection:"column", gap:2, flexShrink:0 }}>
                                <div style={{ height:3, background:"#e6e8ee", borderRadius:1 }} />
                                <div style={{ height:3, background:"#eef0f4", borderRadius:1, width:"80%" }} />
                                <div style={{ height:3, background:"#eef0f4", borderRadius:1, width:"65%" }} />
                              </div>
                            </>
                          ) : (
                            <div style={{ height:"93.75%", background:"white", padding:"6px 5px", display:"flex", flexDirection:"column", gap:2, flexShrink:0 }}>
                              <div style={{ fontSize:5, fontWeight:700, color:"#1a1a1c", textTransform:"uppercase", letterSpacing:"0.04em" }}>Comunicados</div>
                              <div style={{ height:3, background:"#e6e8ee", borderRadius:1 }} />
                              <div style={{ height:3, background:"#eef0f4", borderRadius:1, width:"88%" }} />
                              <div style={{ height:3, background:"#eef0f4", borderRadius:1, width:"72%" }} />
                              <div style={{ height:3, background:"#eef0f4", borderRadius:1, width:"94%" }} />
                            </div>
                          )}
                          <div style={{ height:"6.25%", background:"#1f3a7a", flexShrink:0 }} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Tela selecionada */}
              <div style={{ padding:"10px 14px", background:"rgba(30,58,138,0.06)", border:"0.5px solid rgba(30,58,138,0.15)", borderRadius:10, display:"flex", flexDirection:"column", gap:4 }}>
                <div style={{ fontSize:12.5 }}><b style={{ color:"#1e3a8a" }}>Tela selecionada:</b> <span style={{ color:"#1A1A1C", fontWeight:500 }}>{footerScreen.name || "(sem nome)"}</span></div>
                <div style={{ fontSize:12.5 }}><b style={{ color:"#1e3a8a" }}>Token:</b> <code style={{ fontFamily:"ui-monospace,monospace", color:"#C5221F", fontSize:12 }}>{footerScreen.token}</code></div>
              </div>

              {/* Aplicar para */}
              <div>
                <div style={{ fontSize:13, fontWeight:600, marginBottom:8, color:"#1A1A1C" }}>Aplicar para</div>
                <div style={{ position:"relative" }}>
                  <select value={footerScope} onChange={e => handleScopeChange(e.target.value)} disabled={savingFooter}
                    style={{ width:"100%", padding:"10px 36px 10px 12px", borderRadius:10, border:"1px solid rgba(20,20,22,0.12)", background:"rgba(20,20,22,0.03)", fontSize:13, color:"#1A1A1C", cursor:"pointer", appearance:"none", WebkitAppearance:"none", fontFamily:"inherit", outline:"none" }}>
                    <option value="single">Apenas esta tela</option>
                    <option value="condo" disabled={!footerScreen.superlogicaCondoId}>Todas as telas do condomínio {footerScreen.superlogicaCondoName ? `(${footerScreen.superlogicaCondoName})` : "(não vinculado)"}</option>
                    <option value="city" disabled={!footerScreen.city}>Todas as telas de {footerScreen.city || "(sem cidade)"}/{footerScreen.uf || "?"}</option>
                    <option value="all-online">Todas as telas online</option>
                    <option value="all">Todas as telas</option>
                  </select>
                  <span style={{ position:"absolute", right:12, top:"50%", transform:"translateY(-50%)", pointerEvents:"none", fontSize:12, color:"#86868B" }}>↓</span>
                </div>
                {affectedScreens.length > 0 && (
                  <div style={{ marginTop:8, padding:"10px 12px", background:"rgba(20,20,22,0.03)", border:"0.5px solid rgba(20,20,22,0.07)", borderRadius:10, fontSize:12, color:"#4A4A50" }}>
                    <div style={{ fontWeight:600, marginBottom: affectedScreens.length > 1 ? 6 : 0 }}>
                      {affectedScreens.length === 1 ? `1 tela será atualizada: ${footerScreen.name || "(sem nome)"}` : `${affectedScreens.length} telas serão atualizadas`}
                    </div>
                    {affectedScreens.length > 1 && (
                      <div style={{ maxHeight:160, overflowY:"auto", fontSize:11.5 }}>
                        {affectedScreens.slice(0,30).map((s, i) => (
                          <div key={s._id} style={{ padding:"3px 0", borderTop: i > 0 ? "0.5px solid rgba(20,20,22,0.05)" : "none" }}>
                            <b>{s.name || "(sem nome)"}</b>{s.city ? ` · ${s.city}/${s.uf}` : ""} <span style={{ color:"#86868B" }}>{s.token?.substring(0,15)}…</span>
                          </div>
                        ))}
                        {affectedScreens.length > 30 && <div style={{ color:"#86868B", fontStyle:"italic", marginTop:4 }}>…e mais {affectedScreens.length - 30} tela(s)</div>}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div style={{ padding:"14px 22px", borderTop:"0.5px solid rgba(20,20,22,0.07)", display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, flexShrink:0 }}>
              <button onClick={closeFooterModal} disabled={savingFooter}
                style={{ padding:"9px", borderRadius:10, border:"1px solid rgba(20,20,22,0.12)", background:"transparent", color:"#1A1A1C", fontWeight:500, fontSize:13, cursor:"pointer", fontFamily:"inherit", display:"flex", alignItems:"center", justifyContent:"center", opacity: savingFooter ? 0.5 : 1 }}>
                Cancelar
              </button>
              <button onClick={handleSaveFooter} disabled={savingFooter}
                style={{ padding:"9px", borderRadius:10, border:"none", background:"#1e3a8a", color:"#fff", fontWeight:600, fontSize:13, cursor:"pointer", fontFamily:"inherit", display:"flex", alignItems:"center", justifyContent:"center", gap:6, opacity: savingFooter ? 0.6 : 1 }}>
                <CheckIcon style={{ width:14, height:14 }} /> {savingFooter ? "Salvando…" : "Salvar"}
              </button>
            </div>
          </div>
        </>
      )}
      {/* Drawer de Saúde do Sistema */}
      {showHealthModal && (
        <>
          {/* Overlay */}
          <div
            onClick={closeHealthModal}
            style={{ position: "fixed", inset: 0, background: "rgba(20,20,22,0.4)", backdropFilter: "blur(3px)", WebkitBackdropFilter: "blur(3px)", zIndex: 1050, animation: "sl-fadein 0.2s ease" }}
          />
          {/* Drawer panel */}
          <div style={{ position: "fixed", top: 0, right: 0, bottom: 0, width: 580, maxWidth: "100vw", background: "#f9f8f6", zIndex: 1051, display: "flex", flexDirection: "column", boxShadow: "-12px 0 40px rgba(20,20,22,0.14)", animation: "sl-slidein 0.3s cubic-bezier(0.32,0.72,0,1)", fontFamily: "-apple-system,BlinkMacSystemFont,'SF Pro Text',system-ui,sans-serif" }}>

            {/* Header do drawer */}
            <div style={{ padding: "1.25rem 1.5rem", borderBottom: "1px solid rgba(20,20,22,0.07)", background: "#f9f8f6", flexShrink: 0 }}>
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "#86868B", marginBottom: 4 }}>Saúde do Sistema</div>
                  <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: "-0.03em", color: "#1A1A1C", lineHeight: 1.1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{healthScreen?.name || "—"}</div>
                  {healthScreen && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 6 }}>
                      <div>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", fontSize: 11.5, fontWeight: 500, borderRadius: 999, background: healthScreen.status === "online" ? "rgba(30,142,62,0.10)" : "rgba(20,20,22,0.06)", color: healthScreen.status === "online" ? "#1E8E3E" : "#86868B" }}>
                          <span style={{ width: 5, height: 5, borderRadius: "50%", background: healthScreen.status === "online" ? "#1E8E3E" : "#86868B", display: "inline-block" }} />
                          {healthScreen.status === "online" ? "Online" : "Offline"}
                        </span>
                      </div>
                      {(healthScreen.superlogicaCondoName || healthScreen.city) && (
                        <div style={{ fontSize: 12, color: "#86868B", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {[healthScreen.superlogicaCondoName, healthScreen.city && `${healthScreen.city}${healthScreen.uf ? `/${healthScreen.uf}` : ""}`].filter(Boolean).join(" · ")}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {/* Action buttons + close */}
                <div style={{ display: "flex", gap: 6, flexShrink: 0, alignItems: "flex-start" }}>
                  {healthScreen && [
                    { onClick: () => openEditNameModal(healthScreen),                                                       title: "Editar nome",          icon: <PencilIcon style={{ width: 14, height: 14 }} />,        bg: "rgba(88,86,214,0.09)",  color: "#5856d6" },
                    { onClick: () => { setBindReturnScreen(healthScreen); closeHealthModal(); openBindModal(healthScreen); }, title: "Associar condomínio",  icon: <LinkIcon style={{ width: 14, height: 14 }} />,          bg: "rgba(30,58,138,0.09)",  color: "#1e3a8a" },
                    { onClick: () => checkBrowserHealth(healthScreen._id, "restart"),                                        title: "Reiniciar browser",    icon: <GlobeAltIcon style={{ width: 14, height: 14 }} />,      bg: "rgba(30,142,62,0.09)",  color: "#1E8E3E", disabled: browserHealthLoading },
                    { onClick: () => checkBrowserHealth(healthScreen._id, "reboot"),                                         title: "Reboot da máquina",    icon: <ArrowPathIcon style={{ width: 14, height: 14 }} />,     bg: "rgba(180,83,9,0.09)",   color: "#B45309", disabled: browserHealthLoading },
                    { onClick: () => checkBrowserHealth(healthScreen._id, "clear-cache"),                                    title: "Limpar cache remoto",  icon: <WrenchScrewdriverIcon style={{ width: 14, height: 14 }} />, bg: "rgba(107,33,168,0.09)", color: "#6B21A8", disabled: browserHealthLoading },
                    { onClick: () => handleForceSync(healthScreen._id),                                                      title: "Forçar sincronização", icon: <ArrowDownTrayIcon style={{ width: 14, height: 14 }} />, bg: "rgba(30,58,138,0.09)",  color: "#1e3a8a" },
                    { onClick: () => { handleDeleteScreen(healthScreen); closeHealthModal(); },                               title: "Deletar tela",         icon: <TrashIcon style={{ width: 14, height: 14 }} />,         bg: "rgba(197,34,31,0.09)",  color: "#C5221F" },
                  ].map((btn, i) => (
                    <button key={i} onClick={btn.onClick} title={btn.title} disabled={btn.disabled}
                      style={{ width: 30, height: 30, border: "1px solid rgba(20,20,22,0.07)", borderRadius: 8, background: btn.bg, color: btn.color, cursor: "pointer", display: "grid", placeItems: "center", opacity: btn.disabled ? 0.4 : 1, flexShrink: 0 }}>
                      {btn.icon}
                    </button>
                  ))}
                  <button onClick={closeHealthModal} title="Fechar"
                    style={{ width: 30, height: 30, border: "1px solid rgba(20,20,22,0.07)", borderRadius: 8, background: "rgba(20,20,22,0.04)", color: "#1A1A1C", cursor: "pointer", display: "grid", placeItems: "center", flexShrink: 0 }}>
                    <XMarkIcon style={{ width: 15, height: 15 }} />
                  </button>
                </div>
              </div>
            </div>

            {/* Body scrollável */}
            <div style={{ flex: 1, overflowY: "auto", padding: "1.25rem 1.5rem", display: "flex", flexDirection: "column", gap: 12 }}>
          {healthScreen ? (
            <>
              {/* Card 1: Preview + campos de info */}
              <div style={{ display: "grid", gridTemplateColumns: "180px 1fr" }}>
                {/* Lado esquerdo: preview 9:16 — réplica do player no viewport real da TV (540×960), reduzida via transform */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "0 12px 0 0" }}>
                  {(() => {
                    const notice = healthPreviewFallback?.noticePreview
                      || (Array.isArray(healthScreen.nowPlayingNoticeTitles) && healthScreen.nowPlayingNoticeTitles.length > 0
                        ? { title: healthScreen.nowPlayingNoticeTitles[0], text: "", imageUrl: null }
                        : null);
                    const mediaUrl = healthScreen.nowPlayingMediaUrl || healthPreviewFallback?.mediaUrl || null;
                    const layout = (healthScreen.layoutType === "layout1" || !healthScreen.layoutType) ? "layout2" : healthScreen.layoutType;
                    return (
                      <TvScreenPreview
                        width={162}
                        layoutType={healthScreen.layoutType}
                        isAssembly={!!healthPreviewFallback?.isAssembly}
                        mediaUrl={mediaUrl}
                        mediaType={healthScreen.nowPlayingMediaType || healthPreviewFallback?.mediaType || null}
                        notice={notice}
                        footerUrl={healthScreen.footerMediaUrl}
                        isOnline={healthScreen.status === "online"}
                        estimated={!mediaUrl && layout === "layout2" && !healthPreviewFallback?.isAssembly}
                      />
                    );
                  })()}
                </div>
                {/* Lado direito: campos */}
                <div style={{ padding: "16px 18px", display: "flex", flexDirection: "column", gap: 8 }}>
                  {[
                    { label: "Token",    value: <code style={{ fontSize: 12, fontWeight: 600, color: "#C5221F", letterSpacing: "0.02em", fontFamily: "ui-monospace,SFMono-Regular,Menlo,monospace" }}>{healthScreen.token}</code> },
                    { label: "IP Local", value: <span style={{ fontSize: 12.5, fontWeight: 500, color: "#1A1A1C", fontVariantNumeric: "tabular-nums" }}>{healthScreen.playerIp || "—"}</span> },
                    { label: "MAC",      value: <code style={{ fontSize: 11, fontWeight: 500, color: healthScreen.boundMacAddress ? "#1E8E3E" : "#86868B", fontFamily: "ui-monospace,SFMono-Regular,Menlo,monospace" }}>{healthScreen.boundMacAddress || "Não vinculado"}</code> },
                    { label: "Browser",  value: (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                        <span style={{ width: 7, height: 7, borderRadius: "50%", background: healthScreen.browserRunning === true ? "#34c759" : healthScreen.browserRunning === false ? "#C5221F" : "#86868B", flexShrink: 0 }} />
                        <span style={{ fontSize: 12.5, fontWeight: 500, color: "#1A1A1C" }}>{healthScreen.browserRunning === true ? "Online" : healthScreen.browserRunning === false ? "Offline" : "—"}</span>
                      </span>
                    )},
                    { label: "Hardware", value: <span style={{ fontSize: 12.5, fontWeight: 500, color: "#1A1A1C" }}>{healthScreen.osRelease || "—"}</span> },
                    { label: "Latência", value: (
                      <span style={{ fontSize: 12.5, fontVariantNumeric: "tabular-nums" }}>
                        <b style={{ fontWeight: 600, color: (() => { const l = healthScreen.networkLatencyMs; return !l ? "#86868B" : l > 500 ? "#C5221F" : l > 200 ? "#B45309" : "#1A1A1C"; })() }}>{healthScreen.networkLatencyMs ?? "—"}</b>
                        {healthScreen.networkLatencyMs != null && <span style={{ color: "#86868B", marginLeft: 2, fontWeight: 400 }}>ms</span>}
                      </span>
                    )},
                  ].map(row => (
                    <div key={row.label} style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                      <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase", color: "#86868B" }}>{row.label}</span>
                      {row.value}
                    </div>
                  ))}
                </div>
              </div>

              {/* KPIs em tempo real: 2×2 */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                {/* CPU */}
                {(() => {
                  const v = healthScreen.cpuUsagePct;
                  const pct = v != null ? Math.min(100, v) : null;
                  const color = pct == null ? "#86868B" : pct > 90 ? "#C5221F" : pct > 70 ? "#B45309" : "#22c55e";
                  const label = pct == null ? "Aguardando" : pct > 90 ? "Pico de CPU" : pct > 70 ? "Uso elevado" : "Normal";
                  return (
                    <div style={{ background: "rgba(255,255,255,0.72)", borderRadius: 12, border: "1px solid rgba(20,20,22,0.07)", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", padding: "1rem 1.125rem" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: "#86868B", marginBottom: 8 }}>
                        <ComputerDesktopIcon style={{ width: 14, height: 14 }} /> Uso de CPU
                      </div>
                      <div style={{ fontSize: 28, fontWeight: 600, letterSpacing: "-0.02em", marginTop: 4, fontVariantNumeric: "tabular-nums", color: "#1A1A1C", lineHeight: 1.1, marginBottom: 6 }}>
                        {pct != null ? <>{pct.toFixed(1)}<span style={{ fontSize: 14, fontWeight: 400, color: "#86868B", marginLeft: 2 }}>%</span></> : "—"}
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 6 }}>
                        <span style={{ width: 7, height: 7, borderRadius: "50%", background: color, flexShrink: 0 }} />
                        <span style={{ fontSize: 13, fontWeight: 500, color }}>{label}</span>
                      </div>
                      {(() => {
                        const vals = healthHistory.slice(-20).map(s => s.cpuUsagePct ?? 0);
                        if (vals.length < 2) return (
                          <div style={{ height: 4, borderRadius: 999, background: "rgba(20,20,22,0.08)", overflow: "hidden" }}>
                            <div style={{ width: `${pct ?? 0}%`, height: "100%", background: color, borderRadius: 999 }} />
                          </div>
                        );
                        const W = 200, H = 36;
                        const pts = vals.map((v, i) => [+(i / (vals.length - 1) * W).toFixed(1), +(H - (v / 100) * (H - 2)).toFixed(1)]);
                        const pline = pts.map(p => p.join(",")).join(" ");
                        const area = `M0,${H} ${pts.map(p => `L${p[0]},${p[1]}`).join(" ")} L${W},${H} Z`;
                        return (
                          <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width: "100%", height: 36, display: "block", borderRadius: 6 }}>
                            <defs>
                              <linearGradient id="cpuAreaGrad" x1="0" x2="0" y1="0" y2="1">
                                <stop offset="0%" stopColor={color} stopOpacity="0.28" />
                                <stop offset="100%" stopColor={color} stopOpacity="0" />
                              </linearGradient>
                            </defs>
                            <path d={area} fill="url(#cpuAreaGrad)" />
                            <polyline points={pline} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        );
                      })()}
                    </div>
                  );
                })()}

                {/* RAM */}
                {(() => {
                  const used = healthScreen.memoryUsedMb;
                  const total = healthScreen.memoryTotalMb;
                  const pct = used != null && total ? Math.min(100, (used / total) * 100) : null;
                  const color = pct == null ? "#86868B" : pct > 90 ? "#C5221F" : pct > 75 ? "#B45309" : "#22c55e";
                  return (
                    <div style={{ background: "rgba(255,255,255,0.72)", borderRadius: 12, border: "1px solid rgba(20,20,22,0.07)", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", padding: "1rem 1.125rem" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: "#86868B", marginBottom: 8 }}>
                        <svg style={{ width: 14, height: 14 }} viewBox="0 0 20 20" fill="currentColor"><path d="M3 4a1 1 0 000 2h14a1 1 0 100-2H3zm0 4a1 1 0 000 2h14a1 1 0 100-2H3zm0 4a1 1 0 000 2h14a1 1 0 100-2H3z"/></svg>
                        Memória RAM
                      </div>
                      <div style={{ fontSize: 28, fontWeight: 600, letterSpacing: "-0.02em", marginTop: 4, fontVariantNumeric: "tabular-nums", color: "#1A1A1C", lineHeight: 1.1, marginBottom: 4 }}>
                        {used != null ? <>{Math.round(used)}<span style={{ fontSize: 14, fontWeight: 400, color: "#86868B", marginLeft: 2 }}>MB</span></> : "—"}
                      </div>
                      {total && <div style={{ fontSize: 12, color: "#86868B", marginBottom: 8 }}>de {Math.round(total)} MB</div>}
                      <div style={{ height: 4, borderRadius: 999, background: "rgba(20,20,22,0.08)", overflow: "hidden", marginTop: total ? 0 : 8 }}>
                        <div style={{ width: `${pct ?? 0}%`, height: "100%", background: color, borderRadius: 999, transition: "width 0.4s" }} />
                      </div>
                    </div>
                  );
                })()}

                {/* Disco */}
                {(() => {
                  const free = healthScreen.diskFreeGb;
                  const total = healthScreen.diskTotalGb;
                  const usedPct = free != null && total ? Math.min(100, ((total - free) / total) * 100) : null;
                  const color = usedPct == null ? "#86868B" : usedPct > 90 ? "#C5221F" : usedPct > 75 ? "#B45309" : "#22c55e";
                  return (
                    <div style={{ background: "rgba(255,255,255,0.72)", borderRadius: 12, border: "1px solid rgba(20,20,22,0.07)", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", padding: "1rem 1.125rem" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: "#86868B", marginBottom: 8 }}>
                        <ArrowDownTrayIcon style={{ width: 14, height: 14 }} /> Espaço em Disco
                      </div>
                      <div style={{ fontSize: 28, fontWeight: 600, letterSpacing: "-0.02em", marginTop: 4, fontVariantNumeric: "tabular-nums", color: "#1A1A1C", lineHeight: 1.1, marginBottom: 4 }}>
                        {free != null && total != null ? <>{(total - free).toFixed(1)}<span style={{ fontSize: 14, fontWeight: 400, color: "#86868B", marginLeft: 2 }}>GB</span></> : "—"}
                      </div>
                      {total && <div style={{ fontSize: 12, color: "#86868B", marginBottom: 8 }}>de {total.toFixed(1)} GB</div>}
                      <div style={{ height: 4, borderRadius: 999, background: "rgba(20,20,22,0.08)", overflow: "hidden", marginTop: total ? 0 : 8 }}>
                        <div style={{ width: `${usedPct ?? 0}%`, height: "100%", background: color, borderRadius: 999, transition: "width 0.4s" }} />
                      </div>
                    </div>
                  );
                })()}

                {/* Temperatura */}
                {(() => {
                  const t = healthScreen.cpuTemp;
                  const color = t == null ? "#86868B" : t >= 70 ? "#C5221F" : "#22c55e";
                  const label = t == null ? "Aguardando" : t >= 70 ? "Aquecimento" : "Normal";
                  return (
                    <div style={{ background: "rgba(255,255,255,0.72)", borderRadius: 12, border: "1px solid rgba(20,20,22,0.07)", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", padding: "1rem 1.125rem" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: "#86868B", marginBottom: 8 }}>
                        <BoltIcon style={{ width: 14, height: 14 }} /> Temperatura CPU
                      </div>
                      <div style={{ fontSize: 28, fontWeight: 600, letterSpacing: "-0.02em", marginTop: 4, fontVariantNumeric: "tabular-nums", color: "#1A1A1C", lineHeight: 1.1, marginBottom: 6 }}>
                        {t != null ? <>{t.toFixed(1)}<span style={{ fontSize: 14, fontWeight: 400, color: "#86868B", marginLeft: 2 }}>°C</span></> : "—"}
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 6 }}>
                        <span style={{ width: 7, height: 7, borderRadius: "50%", background: color, flexShrink: 0 }} />
                        <span style={{ fontSize: 13, fontWeight: 500, color }}>{label}</span>
                      </div>
                      {(() => {
                        const vals = healthHistory.slice(-20).map(s => s.cpuTemp ?? 0);
                        if (vals.length < 2) return t != null ? (
                          <div style={{ height: 4, borderRadius: 999, background: "rgba(20,20,22,0.08)", overflow: "hidden" }}>
                            <div style={{ width: `${Math.min(100, (t / 100) * 100)}%`, height: "100%", background: color, borderRadius: 999 }} />
                          </div>
                        ) : null;
                        const W = 200, H = 36;
                        const maxT = Math.max(...vals, 60);
                        const pts = vals.map((v, i) => [+(i / (vals.length - 1) * W).toFixed(1), +(H - (v / maxT) * (H - 2)).toFixed(1)]);
                        const pline = pts.map(p => p.join(",")).join(" ");
                        const area = `M0,${H} ${pts.map(p => `L${p[0]},${p[1]}`).join(" ")} L${W},${H} Z`;
                        const sparkColor = "#f59e0b";
                        return (
                          <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width: "100%", height: 36, display: "block", borderRadius: 6 }}>
                            <defs>
                              <linearGradient id="tempAreaGrad" x1="0" x2="0" y1="0" y2="1">
                                <stop offset="0%" stopColor={sparkColor} stopOpacity="0.28" />
                                <stop offset="100%" stopColor={sparkColor} stopOpacity="0" />
                              </linearGradient>
                            </defs>
                            <path d={area} fill="url(#tempAreaGrad)" />
                            <polyline points={pline} fill="none" stroke={sparkColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        );
                      })()}
                    </div>
                  );
                })()}
              </div>

              {/* Card 2: Picos + Disponibilidade lado a lado */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                {/* Picos (30 dias) */}
                <div style={{ background: "rgba(255,255,255,0.72)", borderRadius: 12, border: "1px solid rgba(20,20,22,0.07)", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", padding: "0.875rem 1rem" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 10, fontWeight: 600, letterSpacing: "0.07em", textTransform: "uppercase", color: "#86868B", marginBottom: 12 }}>
                    <BoltIcon style={{ width: 12, height: 12 }} /> Picos (30 dias)
                  </div>
                  {healthLoading ? (
                    <div style={{ fontSize: 12, color: "#86868B" }}>Carregando...</div>
                  ) : healthPeaks ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      {[
                        { label: "CPU",  value: healthPeaks.cpuUsagePct?.value != null  ? `${healthPeaks.cpuUsagePct.value.toFixed(1)}%`   : "—", at: healthPeaks.cpuUsagePct?.at,  color: "#B45309" },
                        { label: "Temp", value: healthPeaks.cpuTemp?.value != null       ? `${healthPeaks.cpuTemp.value.toFixed(1)}°C`        : "—", at: healthPeaks.cpuTemp?.at,       color: "#C5221F" },
                        { label: "RAM",  value: healthPeaks.memoryUsedMb?.value != null  ? `${Math.round(healthPeaks.memoryUsedMb.value)} MB` : "—", at: healthPeaks.memoryUsedMb?.at, color: "#1e3a8a" },
                      ].map(r => (
                        <div key={r.label} style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 500, color: "#1A1A1C" }}>{r.label}</div>
                            {r.at && <div style={{ fontSize: 10, color: "#86868B", marginTop: 1 }}>{formatDate(r.at)}</div>}
                          </div>
                          <div style={{ fontSize: 14, fontWeight: 700, color: r.color }}>{r.value}</div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: "#86868B" }}>Sem histórico</div>
                  )}
                </div>

                {/* Disponibilidade */}
                <div style={{ background: "rgba(255,255,255,0.72)", borderRadius: 12, border: "1px solid rgba(20,20,22,0.07)", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", padding: "0.875rem 1rem" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 10, fontWeight: 600, letterSpacing: "0.07em", textTransform: "uppercase", color: "#86868B", marginBottom: 12 }}>
                    <ClockIcon style={{ width: 12, height: 12 }} /> Disponibilidade
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, paddingBottom: 8, borderBottom: "1px solid rgba(20,20,22,0.06)" }}>
                    <span style={{ fontSize: 12, color: "#86868B" }}>Uptime atual</span>
                    <span style={{ fontSize: 14, fontWeight: 700, color: "#1A1A1C" }}>{formatUptime(healthScreen.uptimeSec)}</span>
                  </div>
                  {!uptimeStats ? (
                    <div style={{ fontSize: 12, color: "#86868B" }}>Carregando...</div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {[
                        { label: "24h", val: uptimeStats["24h"]?.online || 0, total: 24 * 3600 * 1000 },
                        { label: "7d",  val: uptimeStats["7d"]?.online  || 0, total: 7  * 24 * 3600 * 1000 },
                        { label: "30d", val: uptimeStats["30d"]?.online || 0, total: 30 * 24 * 3600 * 1000 },
                      ].map(row => {
                        const pct = Math.min(100, Math.round((row.val / row.total) * 100));
                        return (
                          <div key={row.label} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ fontSize: 12, color: "#86868B", minWidth: 22 }}>{row.label}</span>
                            <div style={{ flex: 1, height: 4, borderRadius: 999, background: "rgba(20,20,22,0.08)", overflow: "hidden" }}>
                              <div style={{ width: `${pct}%`, height: "100%", background: "#34c759", borderRadius: 999 }} />
                            </div>
                            <span style={{ fontSize: 11, color: "#1A1A1C", fontWeight: 500, minWidth: 46, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{formatUptimeMs(row.val)}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Histórico Recente */}
              <div style={{ background: "rgba(255,255,255,0.72)", borderRadius: 12, border: "1px solid rgba(20,20,22,0.07)", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", padding: "0.875rem 1rem" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 10, fontWeight: 600, letterSpacing: "0.07em", textTransform: "uppercase", color: "#86868B" }}>
                    <QueueListIcon style={{ width: 12, height: 12 }} /> Histórico Recente
                  </div>
                  <button
                    onClick={() => loadHealthHistory(healthScreen._id)}
                    disabled={healthLoading}
                    style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 10px", borderRadius: 7, border: "1px solid rgba(20,20,22,0.1)", background: "transparent", color: "#1A1A1C", fontSize: 11, fontWeight: 500, cursor: "pointer", fontFamily: "inherit", opacity: healthLoading ? 0.5 : 1 }}
                  >
                    <ArrowPathIcon style={{ width: 11, height: 11 }} /> Atualizar
                  </button>
                </div>
                {healthLoading ? (
                  <div style={{ fontSize: 12, color: "#86868B" }}>Carregando...</div>
                ) : healthHistory.length === 0 ? (
                  <div style={{ fontSize: 12, color: "#86868B" }}>Nenhuma amostra ainda.</div>
                ) : (
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                      <thead>
                        <tr>
                          {["Quando","CPU","Temp","RAM","Disco"].map(h => (
                            <th key={h} style={{ padding: "4px 8px", textAlign: "left", border: "none", borderBottom: "1px solid rgba(20,20,22,0.07)", fontSize: 10, letterSpacing: "0.05em", textTransform: "uppercase", color: "#86868B", fontWeight: 600, whiteSpace: "nowrap" }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {healthHistory.slice(-20).reverse().map(s => (
                          <tr key={s._id}>
                            <td style={{ padding: "6px 8px", border: "none", borderBottom: "1px solid rgba(20,20,22,0.04)", color: "#1A1A1C", whiteSpace: "nowrap" }}>{formatDate(s.sampledAt)}</td>
                            <td style={{ padding: "6px 8px", border: "none", borderBottom: "1px solid rgba(20,20,22,0.04)", fontVariantNumeric: "tabular-nums", color: s.cpuUsagePct > 80 ? "#B45309" : "#1A1A1C" }}>{s.cpuUsagePct != null ? `${s.cpuUsagePct.toFixed(1)}%` : "—"}</td>
                            <td style={{ padding: "6px 8px", border: "none", borderBottom: "1px solid rgba(20,20,22,0.04)", fontVariantNumeric: "tabular-nums", color: s.cpuTemp >= 70 ? "#C5221F" : "#1A1A1C" }}>{s.cpuTemp != null ? `${s.cpuTemp.toFixed(1)}°` : "—"}</td>
                            <td style={{ padding: "6px 8px", border: "none", borderBottom: "1px solid rgba(20,20,22,0.04)", fontVariantNumeric: "tabular-nums", color: "#1A1A1C" }}>{s.memoryUsedMb != null ? Math.round(s.memoryUsedMb) : "—"}</td>
                            <td style={{ padding: "6px 8px", border: "none", borderBottom: "1px solid rgba(20,20,22,0.04)", fontVariantNumeric: "tabular-nums", color: "#1A1A1C" }}>{s.diskFreeGb != null ? `${s.diskFreeGb.toFixed(1)} GB` : "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div style={{ background: "rgba(255,255,255,0.72)", borderRadius: 12, border: "1px solid rgba(20,20,22,0.07)", padding: "2rem", textAlign: "center" }}>
              <ClockIcon style={{ width: 40, height: 40, color: "#86868B", marginBottom: "0.75rem" }} />
              <div style={{ fontSize: 15, fontWeight: 600, color: "#1A1A1C", marginBottom: 6 }}>Aguardando primeiros dados</div>
              <div style={{ fontSize: 13, color: "#86868B" }}>Os dados chegam a cada heartbeat do player</div>
            </div>
          )}

            </div>{/* fim body scrollável */}

            {/* Footer do drawer */}
            <div style={{ padding: "14px 22px", borderTop: "0.5px solid rgba(20,20,22,0.07)", background: "#f9f8f6", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, flexShrink: 0 }}>
              <button onClick={closeHealthModal} style={{ padding: "9px", borderRadius: 10, border: "1px solid rgba(20,20,22,0.12)", background: "transparent", color: "#1A1A1C", fontWeight: 500, fontSize: 13, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center" }}>
                Fechar
              </button>
              <button onClick={closeHealthModal} style={{ padding: "9px", borderRadius: 10, border: "none", background: "#1e3a8a", color: "#fff", fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                <ComputerDesktopIcon style={{ width: 14, height: 14 }} /> Abrir painel completo
              </button>
            </div>

          </div>{/* fim drawer panel */}
        </>
      )}


      {/* Modal de Aviso de Comunicados Incompatíveis com o Novo Layout — estilo Apple */}
      <Modal
        show={layoutNoticeWarning.show}
        onHide={() => layoutNoticeWarning.onConfirm && layoutNoticeWarning.onConfirm(false)}
        centered
        backdrop="static"
        className="layout-warning-modal"
        backdropClassName="layout-warning-backdrop"
      >
        <div style={{
          borderRadius: '12px',
          overflow: 'hidden',
          backgroundColor: '#fff',
          boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
        }}>
          <div style={{ textAlign: 'center', padding: '24px 20px 0 20px' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              backgroundColor: '#fff3cd',
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}><ExclamationTriangleIcon style={{ width: 24, height: 24, color: '#856404' }} /></div>
          </div>
          <Modal.Header style={{ border: 'none', padding: '4px 20px 0 20px', display: 'block', textAlign: 'center' }}>
            <Modal.Title style={{ fontSize: '13px', fontWeight: '700', color: '#000', lineHeight: '1.4' }}>
              Comunicados incompatíveis com o novo layout
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ padding: '8px 20px 4px 20px' }}>
            <p style={{ fontSize: '11px', color: '#000', lineHeight: '1.6', textAlign: 'center', marginBottom: '12px' }}>
              Ao mudar para <strong>{layoutTypeLabel(layoutNoticeWarning.newLayout)}</strong>, os comunicados abaixo
              {' '}<strong>não serão exibidos</strong> nesta tela pois a imagem é incompatível:
            </p>
            <div style={{
              maxHeight: '200px',
              overflowY: 'auto',
              borderRadius: '8px',
              backgroundColor: '#f9f9f9',
              border: '1px solid #e8e8ed',
            }}>
              {layoutNoticeWarning.notices.map((n, i) => (
                <div
                  key={n._id || i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '7px 12px',
                    borderBottom: i < layoutNoticeWarning.notices.length - 1 ? '1px solid #e8e8ed' : 'none',
                    gap: '8px',
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <strong style={{ fontSize: '12px', color: '#1c1c1e', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {n.title}
                    </strong>
                    {n.imageAssetId?.compatibleLayouts?.includes('layout4') ? (
                      <span style={{ fontSize: '11px', color: '#6e6e73' }}>Imagem Retrato Comunicados</span>
                    ) : (
                      <span style={{ fontSize: '11px', color: '#6e6e73' }}>Imagem Paisagem</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <p style={{ marginTop: '10px', marginBottom: 0, color: '#6e6e73', fontSize: '11px', textAlign: 'center', lineHeight: '1.5' }}>
              Deseja continuar mesmo assim?
            </p>
          </Modal.Body>
          <div style={{ display: 'flex', borderTop: '1px solid #e8e8ed', marginTop: '16px' }}>
            <button
              onClick={() => layoutNoticeWarning.onConfirm && layoutNoticeWarning.onConfirm(false)}
              style={{ flex: 1, padding: '12px', border: 'none', borderRight: '1px solid #e8e8ed', background: 'transparent', fontSize: '13px', fontWeight: '400', color: '#007AFF', cursor: 'pointer' }}
            >
              Cancelar
            </button>
            <button
              onClick={() => layoutNoticeWarning.onConfirm && layoutNoticeWarning.onConfirm(true)}
              style={{ flex: 1, padding: '12px', border: 'none', background: 'transparent', fontSize: '13px', fontWeight: '700', color: '#FF9500', cursor: 'pointer' }}
            >
              Mudar mesmo assim
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal de Confirmação - Estilo macOS */}
      <Modal 
        show={confirmModal.show} 
        onHide={() => confirmModal.onConfirm && confirmModal.onConfirm(false)} 
        centered
        backdrop="static"
      >
        <div style={{
          borderRadius: '12px',
          overflow: 'hidden',
          backgroundColor: '#fff',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.25)',
          minWidth: '400px',
          maxWidth: '500px'
        }}>
          <Modal.Header style={{ 
            border: 'none', 
            padding: '20px 20px 8px 20px',
            display: 'block'
          }}>
            <Modal.Title style={{ 
              width: '100%', 
              textAlign: 'left', 
              fontSize: '13px',
              fontWeight: '700',
              color: '#000',
              letterSpacing: '0',
              lineHeight: '1.4',
              marginBottom: '0'
            }}>
              {confirmModal.title}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ 
            textAlign: 'left', 
            padding: '8px 20px 20px 20px'
          }}>
            <p style={{ 
              whiteSpace: 'pre-line', 
              margin: 0,
              color: '#000',
              fontSize: '11px',
              fontWeight: '400',
              lineHeight: '1.6'
            }}>
              {confirmModal.message}
            </p>
          </Modal.Body>
          <div style={{
            display: 'flex',
            gap: '12px',
            padding: '0 20px 20px 20px',
            justifyContent: 'flex-end'
          }}>
            <Button 
              onClick={() => confirmModal.onConfirm && confirmModal.onConfirm(false)}
              style={{
                minWidth: '80px',
                borderRadius: '6px',
                padding: '6px 20px',
                fontWeight: '400',
                border: '1px solid #d1d1d6',
                backgroundColor: '#fff',
                color: '#000',
                fontSize: '13px',
                letterSpacing: '0',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.06)'
              }}
            >
              Cancelar
            </Button>
            <Button 
              onClick={() => confirmModal.onConfirm && confirmModal.onConfirm(true)}
              style={{
                minWidth: '80px',
                borderRadius: '6px',
                padding: '6px 20px',
                fontWeight: '400',
                border: 'none',
                backgroundColor: '#007AFF',
                color: '#fff',
                fontSize: '13px',
                letterSpacing: '0',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.06)'
              }}
            >
              Confirmar
            </Button>
          </div>
        </div>
      </Modal>

      {/* Toast de notificações */}
      <ToastNotification
        show={toast.show}
        onClose={() => setToast({ ...toast, show: false })}
        title={toast.title}
        message={toast.message}
        variant={toast.variant}
      />
    </div>
  );
}
